/* eslint-disable unicorn/numeric-separators-style */
import { StoryData } from '@cinch-nx/data-storyblok'
import type {
  StoryBlokBulletList,
  StoryBlokListItem,
  StoryBlokOrderedList,
  StoryBlokParagraph,
  StoryBlokRichText,
  StoryBlokRichTextTypes,
  StoryBlokText,
} from '@cinch-nx/data-storyblok'
import { PolicyPageStoryContent } from '../types/policy-page'

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null

const getNodeType = (node: unknown): string | undefined => {
  if (!isRecord(node)) return undefined
  const type = node.type
  return typeof type === 'string' ? type : undefined
}

type NonListRichText = Exclude<
  StoryBlokRichTextTypes,
  StoryBlokBulletList | StoryBlokOrderedList
>

function prefixFirstParagraph(
  nodes: NonListRichText[],
  prefix: string,
  options?: {
    indentLevel?: number
    boldPrefix?: boolean
  },
): NonListRichText[] {
  const indentLevel = options?.indentLevel ?? 0
  const indentText = indentLevel > 0 ? '\u00A0'.repeat(indentLevel * 4) : ''

  const indentTextNode: StoryBlokText | null = indentText
    ? { type: 'text', text: indentText }
    : null

  const prefixTextNode: StoryBlokText = {
    type: 'text',
    text: prefix,
    marks: options?.boldPrefix ? [{ type: 'bold' }] : undefined,
  }

  if (nodes.length === 0) {
    return [
      {
        type: 'paragraph',
        content: [...(indentTextNode ? [indentTextNode] : []), prefixTextNode],
      },
    ]
  }

  const [first, ...rest] = nodes
  if (first.type === 'paragraph') {
    const paragraph = first as StoryBlokParagraph
    return [
      {
        type: 'paragraph',
        content: [
          ...(indentTextNode ? [indentTextNode] : []),
          prefixTextNode,
          ...(paragraph.content ?? []),
        ],
      },
      ...rest,
    ]
  }

  return [
    {
      type: 'paragraph',
      content: [...(indentTextNode ? [indentTextNode] : []), prefixTextNode],
    },
    ...nodes,
  ]
}

function sanitizeNode(
  node: unknown,
  insideListItem: boolean,
  listItemPath: number[],
): StoryBlokRichTextTypes[] {
  const type = getNodeType(node)

  if (
    type === 'paragraph' ||
    type === 'heading' ||
    type === 'blockquote' ||
    type === 'blok'
  ) {
    return [node as StoryBlokRichTextTypes]
  }

  if (type === 'ordered_list' || type === 'bullet_list') {
    if (insideListItem) {
      return flattenListToParagraphs(node, listItemPath)
    }
    return [sanitizeList(node, listItemPath)]
  }

  return []
}

function sanitizeListItem(
  node: unknown,
  listItemPath: number[],
): StoryBlokListItem {
  const content: StoryBlokRichTextTypes[] = []

  if (
    isRecord(node) &&
    node.type === 'list_item' &&
    Array.isArray(node.content)
  ) {
    for (const child of node.content) {
      content.push(...sanitizeNode(child, true, listItemPath))
    }
  }

  return {
    type: 'list_item',
    content: content as NonListRichText[],
  }
}

function sanitizeList(
  node: unknown,
  parentPath: number[],
): StoryBlokOrderedList | StoryBlokBulletList {
  const type = getNodeType(node)

  if (type === 'ordered_list') {
    const order =
      isRecord(node) &&
      isRecord(node.attrs) &&
      typeof node.attrs.order === 'number'
        ? node.attrs.order
        : 1

    const items: StoryBlokListItem[] = []
    if (isRecord(node) && Array.isArray(node.content)) {
      let index = 0
      for (const item of node.content) {
        items.push(sanitizeListItem(item, [...parentPath, order + index]))
        index += 1
      }
    }

    return {
      type: 'ordered_list',
      attrs: { order },
      content: items,
    }
  }

  const items: StoryBlokListItem[] = []
  if (isRecord(node) && Array.isArray(node.content)) {
    for (const item of node.content) {
      items.push(sanitizeListItem(item, parentPath))
    }
  }

  return {
    type: 'bullet_list',
    content: items,
  }
}

function flattenListToParagraphs(
  node: unknown,
  parentPath: number[],
): NonListRichText[] {
  const type = getNodeType(node)
  if (type !== 'ordered_list' && type !== 'bullet_list') return []

  const orderStart =
    type === 'ordered_list' &&
    isRecord(node) &&
    isRecord(node.attrs) &&
    typeof node.attrs.order === 'number'
      ? node.attrs.order
      : 1

  const listItems: unknown[] =
    isRecord(node) && Array.isArray(node.content) ? node.content : []

  const flattened: NonListRichText[] = []
  let index = 0

  for (const listItem of listItems) {
    const itemNumber = orderStart + index
    const itemPath =
      type === 'ordered_list' ? [...parentPath, itemNumber] : parentPath
    const sanitizedItem = sanitizeListItem(listItem, itemPath)
    const prefix = type === 'ordered_list' ? `${itemPath.join('.')} ` : '• '

    const indentLevel =
      type === 'ordered_list'
        ? Math.max(itemPath.length - 1, 0)
        : parentPath.length

    flattened.push(
      ...prefixFirstParagraph(sanitizedItem.content, prefix, {
        indentLevel,
        boldPrefix: type === 'ordered_list',
      }),
    )
    index += 1
  }

  return flattened
}

function flattenNestedListsInStoryblokRichText(
  input: unknown,
): StoryBlokRichText {
  if (
    !isRecord(input) ||
    input.type !== 'doc' ||
    !Array.isArray(input.content)
  ) {
    return { type: 'doc', content: [] }
  }

  return {
    type: 'doc',
    content: (input.content as unknown[]).flatMap((n) =>
      sanitizeNode(n, false, []),
    ),
  }
}

export const termsAndConditionsFallback: StoryData<PolicyPageStoryContent> = {
  name: 'terms and conditions',
  created_at: '2022-08-08T09:45:39.597Z',
  published_at: '2025-10-23T08:20:18.237Z',
  id: 173135440,
  uuid: '043bd45d-0551-4cc7-8cf6-817649d4eb40',
  content: {
    _uid: '77f2fbd5-f8ff-41a9-b2b3-849fcffddc46',
    body: flattenNestedListsInStoryblokRichText({
      type: 'doc',
      content: [
        {
          type: 'paragraph',
          content: [
            {
              text: 'We’re removing the faff from changing and managing vehicles, and we also want to make our legal documents a cinch for anyone to understand. If you have any doubts or suggestions regarding any of them, please contact us and we’ll do our best to help.',
              type: 'text',
            },
          ],
        },
        {
          type: 'paragraph',
          content: [
            {
              text: 'These Terms apply to all purchases of a used car through cinch, even where you collect your used car from one of our physical collection sites.',
              type: 'text',
              marks: [
                {
                  type: 'styled',
                  attrs: {
                    class: '',
                  },
                },
                {
                  type: 'bold',
                },
              ],
            },
          ],
        },
        {
          type: 'paragraph',
          content: [
            {
              text: 'If you are looking at a new car advertised on cinch, your attention is drawn to Section 1.3 below.',
              type: 'text',
              marks: [
                {
                  type: 'styled',
                  attrs: {
                    class: '',
                  },
                },
                {
                  type: 'bold',
                },
              ],
            },
          ],
        },
        {
          type: 'ordered_list',
          attrs: {
            order: 1,
          },
          content: [
            {
              type: 'list_item',
              content: [
                {
                  type: 'paragraph',
                  content: [
                    {
                      text: 'These terms',
                      type: 'text',
                      marks: [
                        {
                          type: 'bold',
                        },
                      ],
                    },
                  ],
                },
                {
                  type: 'ordered_list',
                  attrs: {
                    order: 1,
                  },
                  content: [
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'These are the terms and conditions (',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                            {
                              text: 'Terms',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                                {
                                  type: 'bold',
                                },
                              ],
                            },
                            {
                              text: ') that apply when you:',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                          ],
                        },
                        {
                          type: 'ordered_list',
                          attrs: {
                            order: 1,
                          },
                          content: [
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'buy a used car from cinch (',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                      ],
                                    },
                                    {
                                      text: 'Car',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                        {
                                          type: 'bold',
                                        },
                                      ],
                                    },
                                    {
                                      text: ');',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                      ],
                                    },
                                  ],
                                },
                              ],
                            },
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'part-exchange your current vehicle as payment towards your Car; and/or',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                      ],
                                    },
                                  ],
                                },
                              ],
                            },
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'buy other products related to the purchase of your Car. ',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                      ],
                                    },
                                  ],
                                },
                              ],
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'These Terms apply to purchases ',
                              type: 'text',
                            },
                            {
                              text: 'of any used Car ',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                            {
                              text: 'made using our website www.cinch.co.uk or mobile app (',
                              type: 'text',
                            },
                            {
                              text: 'Site',
                              type: 'text',
                              marks: [
                                {
                                  type: 'bold',
                                },
                              ],
                            },
                            {
                              text: '), by phone or any other means and whether you are in one of our physical cinch sites (each referred to in these Terms as a ',
                              type: 'text',
                            },
                            {
                              text: 'Store',
                              type: 'text',
                              marks: [
                                {
                                  type: 'bold',
                                },
                              ],
                            },
                            {
                              text: ') or at any other location when you place your order.',
                              type: 'text',
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'We may use our Site to advertise new vehicles that are available to purchase from Marshall Motor Group Limited (a company registered in England and Wales, company number 00295579) (',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                            {
                              text: 'Marshall',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                                {
                                  type: 'bold',
                                },
                              ],
                            },
                            {
                              text: '). Marshall and cinch are part of the same group of companies. If you choose to purchase a new vehicle from Marshall after using our Site, these Terms will not apply to your purchase. The Marshall Terms and Conditions of Sale and Supply will apply to your new vehicle purchase which can be found ',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                            {
                              text: 'here',
                              type: 'text',
                              marks: [
                                {
                                  type: 'link',
                                  attrs: {
                                    href: 'https://www.marshall.co.uk/site/terms-and-conditions/ ',
                                    uuid: null,
                                    anchor: null,
                                    target: '_self',
                                    linktype: 'url',
                                  },
                                },
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                            {
                              text: '.',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'These Terms may change from time to time so make sure you check these Terms each time you buy a Car from cinch.',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: 'rgb(33, 33, 33)',
                                  },
                                },
                              ],
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'Please make sure you read these Terms carefully before you place an order. We suggest that you also keep a copy of these Terms for the future. These Terms will explain how we will sell used cars, how you may cancel the Agreement with us, additional products which you may choose to purchase and what to do if you have an issue with your Car.',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'You can access our terms of use ',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                            {
                              text: 'here',
                              type: 'text',
                              marks: [
                                {
                                  type: 'link',
                                  attrs: {
                                    href: 'https://www.cinch.co.uk/policies/terms-of-use',
                                    uuid: null,
                                    anchor: null,
                                    target: '_self',
                                    linktype: 'url',
                                  },
                                },
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                            {
                              text: ' which apply when you browse the Site (or any other cinch webpages or applications).',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                          ],
                        },
                      ],
                    },
                  ],
                },
              ],
            },
            {
              type: 'list_item',
              content: [
                {
                  type: 'paragraph',
                  content: [
                    {
                      text: 'Who are we?',
                      type: 'text',
                      marks: [
                        {
                          type: 'bold',
                        },
                      ],
                    },
                  ],
                },
                {
                  type: 'ordered_list',
                  attrs: {
                    order: 1,
                  },
                  content: [
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'The Site is operated by Cinch Cars Limited a company registered in England and Wales (company number 11520330). Our registered office is at Form 2, 18 Bartley Wood Business Park, Bartley Way, Hook, Hampshire RG27 9XA. Our registered VAT number is 360909784.',
                              type: 'text',
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'If you would like to get in touch, you can find our contact details ',
                              type: 'text',
                            },
                            {
                              text: 'here',
                              type: 'text',
                              marks: [
                                {
                                  type: 'link',
                                  attrs: {
                                    href: 'https://www.cinch.co.uk/support',
                                    uuid: null,
                                    anchor: null,
                                    target: '_self',
                                    linktype: 'url',
                                  },
                                },
                              ],
                            },
                            {
                              text: '.',
                              type: 'text',
                            },
                          ],
                        },
                      ],
                    },
                  ],
                },
              ],
            },
            {
              type: 'list_item',
              content: [
                {
                  type: 'paragraph',
                  content: [
                    {
                      text: 'Who is selling you the car?',
                      type: 'text',
                      marks: [
                        {
                          type: 'bold',
                        },
                      ],
                    },
                  ],
                },
                {
                  type: 'paragraph',
                  content: [
                    {
                      text: 'cinch will be the supplier of the Car and will sell the Car to you.',
                      type: 'text',
                    },
                  ],
                },
              ],
            },
            {
              type: 'list_item',
              content: [
                {
                  type: 'paragraph',
                  content: [
                    {
                      text: 'The agreement between you and us',
                      type: 'text',
                      marks: [
                        {
                          type: 'bold',
                        },
                      ],
                    },
                  ],
                },
                {
                  type: 'ordered_list',
                  attrs: {
                    order: 1,
                  },
                  content: [
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'When you confirm that you wish to buy a used car on cinch, you are placing an order with us to buy that Car (',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                            {
                              text: 'Order',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                                {
                                  type: 'bold',
                                },
                              ],
                            },
                            {
                              text: '). The Order will confirm the order details including:',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                            {
                              text: ' ',
                              type: 'text',
                            },
                          ],
                        },
                        {
                          type: 'ordered_list',
                          attrs: {
                            order: 1,
                          },
                          content: [
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'your personal details;',
                                      type: 'text',
                                    },
                                  ],
                                },
                              ],
                            },
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'the make and model of the Car;',
                                      type: 'text',
                                    },
                                  ],
                                },
                              ],
                            },
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'the delivery or collection date;',
                                      type: 'text',
                                    },
                                  ],
                                },
                              ],
                            },
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'the delivery or collection location;',
                                      type: 'text',
                                    },
                                  ],
                                },
                              ],
                            },
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'the Purchase Price and the Admin Fee (as defined in section 6 below);',
                                      type: 'text',
                                    },
                                  ],
                                },
                              ],
                            },
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'the amount payable for delivery or collection (the ',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                      ],
                                    },
                                    {
                                      text: 'Transport Charge',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                        {
                                          type: 'bold',
                                        },
                                      ],
                                    },
                                    {
                                      text: ') if a charge applies (see section 5.2 for delivery and section 5.15 for collection);',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                      ],
                                    },
                                  ],
                                },
                              ],
                            },
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'if applicable, reference to your Part Exchange Car (as defined in section 4.4.4); and',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: 'rgb(47, 47, 47)',
                                          },
                                        },
                                      ],
                                    },
                                  ],
                                },
                              ],
                            },
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'the amount payable for any other optional products relating to the purchase of your Car.',
                                      type: 'text',
                                    },
                                  ],
                                },
                              ],
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'We may accept the Order by emailing you to confirm the Order. When we send this email confirmation, a legally binding agreement will be made between you and us (',
                              type: 'text',
                            },
                            {
                              text: 'Agreement',
                              type: 'text',
                              marks: [
                                {
                                  type: 'bold',
                                },
                              ],
                            },
                            {
                              text: '). The Agreement will consist of the Order and these Terms. If you choose to pay for the Order using a Finance Agreement (as defined in section 11 below) with a Lender (as defined in section 11 below) then you will be required to enter into an agreement with the Lender and we will sell the Car to the Lender. Please refer to section 11 for more details.',
                              type: 'text',
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'If there is any inconsistency between the Order and these Terms, then the Order will take priority.',
                              type: 'text',
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'Occasionally, we may become aware after accepting your Order that we are unable to proceed with the Agreement (which includes you buying the Car from us and us buying your current car from you in part exchange), in which case we will notify you by email. Some examples of reasons why we may do this include:',
                              type: 'text',
                            },
                          ],
                        },
                        {
                          type: 'ordered_list',
                          attrs: {
                            order: 1,
                          },
                          content: [
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'the Car you have chosen has already been sold;',
                                      type: 'text',
                                    },
                                  ],
                                },
                              ],
                            },
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'there is an error in the price or description of the Car;',
                                      type: 'text',
                                    },
                                  ],
                                },
                              ],
                            },
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'you have previously purchased and returned a car to us in the 12 months prior to your Order or if we notice an unusual pattern of ordering or returns activity (such as ordering or returning more cars than a typical cinch customer);',
                                      type: 'text',
                                    },
                                  ],
                                },
                              ],
                            },
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'where you are part exchanging your current car (',
                                      type: 'text',
                                    },
                                    {
                                      text: 'Part Exchange Car',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'bold',
                                        },
                                      ],
                                    },
                                    {
                                      text: ') with us and you have provided incorrect details in relation to the Part Exchange Car or any of the circumstances set out in section 10.3 below apply to the Part Exchange Car; ',
                                      type: 'text',
                                    },
                                  ],
                                },
                              ],
                            },
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'you have outstanding finance on your Part Exchange Car and:',
                                      type: 'text',
                                    },
                                  ],
                                },
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'a)  you fail to provide a valid settlement letter to us as explained in section 10 below;',
                                      type: 'text',
                                    },
                                  ],
                                },
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'b)  the outstanding balance of the finance on your Part Exchange Car is different to the outstanding balance which you told us about when placing the Order; or',
                                      type: 'text',
                                    },
                                  ],
                                },
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'c)  the outstanding finance relates to more than one finance provider;',
                                      type: 'text',
                                    },
                                  ],
                                },
                              ],
                            },
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'you have paid for your Car (or settled any other amounts due to us under these Terms) using a payment method that we do not accept; or',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                      ],
                                    },
                                  ],
                                },
                              ],
                            },
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'we have reasonable grounds to suspect fraud is taking place.',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                      ],
                                    },
                                  ],
                                },
                              ],
                            },
                          ],
                        },
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'You should be aware that there may be reasons other than those reasons mentioned in section 4.4 above why we do not accept your Order.',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                          ],
                        },
                        {
                          type: 'paragraph',
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'If we are not able to proceed with your Order, you will not be charged the Purchase Price, any Transport Charge, the Admin Fee for the Car, the deposit (if you have chosen to pay for the Car using finance from a Lender) or any amounts for any other optional products relating to the purchase of your Car. We will also not purchase your Part Exchange Car, settle the outstanding balance of the finance on your Part Exchange Car ',
                              type: 'text',
                            },
                            {
                              text: 'or make any payments to you in respect of your Part Exchange Car.',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                            {
                              text: ' We will contact you to provide and, if possible, try to correct the reason why we have not accepted your Order.',
                              type: 'text',
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'You may cancel the Order without giving a reason before the Car is delivered to you, by calling our customer experience team whose number can be found ',
                              type: 'text',
                            },
                            {
                              text: 'here',
                              type: 'text',
                              marks: [
                                {
                                  type: 'link',
                                  attrs: {
                                    href: 'https://www.cinch.co.uk/support',
                                    uuid: null,
                                    anchor: null,
                                    target: '_self',
                                    linktype: 'url',
                                  },
                                },
                              ],
                            },
                            {
                              text: '.',
                              type: 'text',
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'You may not buy a used car from us if you are doing so in the course of business. You must also be resident in the United Kingdom to purchase a Car from us. Please check our “Home delivery” page ',
                              type: 'text',
                            },
                            {
                              text: 'here',
                              type: 'text',
                              marks: [
                                {
                                  type: 'link',
                                  attrs: {
                                    href: 'https://www.cinch.co.uk/delivery',
                                    uuid: null,
                                    anchor: null,
                                    target: '_self',
                                    linktype: 'url',
                                  },
                                },
                              ],
                            },
                            {
                              text: ' to ensure we deliver to your location. Delivery or collection charges and restrictions may apply, see section 5 below for further information.',
                              type: 'text',
                            },
                          ],
                        },
                      ],
                    },
                  ],
                },
              ],
            },
            {
              type: 'list_item',
              content: [
                {
                  type: 'paragraph',
                  content: [
                    {
                      text: 'Delivery and Collection',
                      type: 'text',
                      marks: [
                        {
                          type: 'bold',
                        },
                      ],
                    },
                  ],
                },
                {
                  type: 'paragraph',
                },
                {
                  type: 'paragraph',
                  content: [
                    {
                      text: 'We offer different delivery and collection options so you can choose which suits you best.',
                      type: 'text',
                    },
                  ],
                },
                {
                  type: 'paragraph',
                },
                {
                  type: 'paragraph',
                  content: [
                    {
                      text: 'Sections 5.1 to 5.13 (inclusive) “',
                      type: 'text',
                      marks: [
                        {
                          type: 'styled',
                          attrs: {
                            class: '',
                          },
                        },
                      ],
                    },
                    {
                      text: 'Part A',
                      type: 'text',
                      marks: [
                        {
                          type: 'styled',
                          attrs: {
                            class: '',
                          },
                        },
                        {
                          type: 'bold',
                        },
                      ],
                    },
                    {
                      text: '” of this section only applies if you choose for us to deliver the Car to you when you place your Order. Delivery is where a cinch driver takes your Car to your residential address.',
                      type: 'text',
                      marks: [
                        {
                          type: 'styled',
                          attrs: {
                            class: '',
                          },
                        },
                      ],
                    },
                  ],
                },
                {
                  type: 'paragraph',
                },
                {
                  type: 'paragraph',
                  content: [
                    {
                      text: 'Sections 5.14 to 5.25 (inclusive) “',
                      type: 'text',
                      marks: [
                        {
                          type: 'styled',
                          attrs: {
                            class: '',
                          },
                        },
                      ],
                    },
                    {
                      text: 'Part B',
                      type: 'text',
                      marks: [
                        {
                          type: 'styled',
                          attrs: {
                            class: '',
                          },
                        },
                        {
                          type: 'bold',
                        },
                      ],
                    },
                    {
                      text: '” of this section only applies if you choose to collect your Car from one of our collection locations, including one of our Stores or locations operated by other companies within our group (such as Marshall and We Buy Any Car) where available.',
                      type: 'text',
                      marks: [
                        {
                          type: 'styled',
                          attrs: {
                            class: '',
                          },
                        },
                      ],
                    },
                  ],
                },
                {
                  type: 'paragraph',
                },
                {
                  type: 'paragraph',
                  content: [
                    {
                      text: 'A: Delivery',
                      type: 'text',
                      marks: [
                        {
                          type: 'bold',
                        },
                      ],
                    },
                  ],
                },
                {
                  type: 'ordered_list',
                  attrs: {
                    order: 1,
                  },
                  content: [
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'Provided we receive payment of the applicable delivery charge, we ',
                              type: 'text',
                            },
                            {
                              text: 'will deliver the Car to you at the delivery location on the delivery date set out in the Order (',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                            {
                              text: 'Delivery',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                                {
                                  type: 'bold',
                                },
                              ],
                            },
                            {
                              text: ').',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'The charge payable by you for Delivery will be set out on the Site at the time you place your Order. Our Delivery charges can change from time to time so please ensure you check what charge will apply each time you look to buy a Car from cinch. The Delivery charge is an example of a Transport Charge in these Terms.',
                              type: 'text',
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'You may change the time and date of Delivery by calling our customer experience team whose number can be found ',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                            {
                              text: 'here',
                              type: 'text',
                              marks: [
                                {
                                  type: 'link',
                                  attrs: {
                                    href: 'https://www.cinch.co.uk/support',
                                    uuid: null,
                                    anchor: null,
                                    target: '_self',
                                    linktype: 'url',
                                  },
                                },
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                            {
                              text: ' ',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                                {
                                  type: 'bold',
                                },
                              ],
                            },
                            {
                              text: 'at any time before 12pm on the day before your agreed Delivery date. We will rearrange the date and time of your Delivery once at no extra cost to you (except for any additional Transport Charge that may be payable under section 5.4.2 below). However, if you need to rearrange the date and time of your Delivery more than once, or you ask to change the date and time of your Delivery after 12pm on the day before your agreed Delivery date, we may charge you an extra administration fee of £150.',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'If you choose to have your Car delivered when you place your Order but later change your mind, and this change means an alternative collection option will apply instead (for example you move from Delivery to an In-Store Collection), then: ',
                              type: 'text',
                            },
                          ],
                        },
                        {
                          type: 'ordered_list',
                          attrs: {
                            order: 1,
                          },
                          content: [
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'if the Transport Charge for the new option is lower than your original choice, we will refund the difference in the Transport Charge you have already paid compared to the Transport Charge of the new option; or',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                      ],
                                    },
                                  ],
                                },
                              ],
                            },
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'if the Transport charge for the new option is higher than your original choice, we reserve the right to charge you for the difference in the Transport Charge.',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                      ],
                                    },
                                  ],
                                },
                                {
                                  type: 'paragraph',
                                },
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'See section 5.15 for more information about collection options.',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                      ],
                                    },
                                  ],
                                },
                              ],
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'We will only deliver the Car to you at the delivery location stated in the Order which must be your residential address. The person stated on the Order must be present at Delivery as we will not deliver the Car to any other person.',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'At Delivery, you must show us either: ',
                              type: 'text',
                            },
                          ],
                        },
                        {
                          type: 'ordered_list',
                          attrs: {
                            order: 1,
                          },
                          content: [
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'a valid photocard UK driving licence and, if the address on your driving licence is different to your residential address stated on the Order, a utility bill or bank statement addressed to your residential address and which is dated in the three months prior to Delivery; or',
                                      type: 'text',
                                    },
                                  ],
                                },
                              ],
                            },
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'your passport and a utility bill or bank statement addressed to your residential address and which is dated in the three months prior to Delivery. ',
                                      type: 'text',
                                    },
                                  ],
                                },
                              ],
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'We may also require you to show us:',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                          ],
                        },
                        {
                          type: 'ordered_list',
                          attrs: {
                            order: 1,
                          },
                          content: [
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'the payment card(s) used to pay for your Car; or',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                      ],
                                    },
                                  ],
                                },
                              ],
                            },
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'if your Part Exchange Car is of a higher value than the Purchase Price of the Car, a payment card, bank statement or other document provided by your bank that contains the bank account information you have provided to us in order for us to pay you the Extra Value (see Part D of section 10 below for more information).',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                      ],
                                    },
                                  ],
                                },
                              ],
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'Please ensure that you bring the correct identification documents and payment card(s) (if applicable) to your delivery appointment as we will not be able to hand the Car over to you without these. If you have any questions on what identification documents you will require please call our customer experience team whose number can be found ',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                            {
                              text: 'here',
                              type: 'text',
                              marks: [
                                {
                                  type: 'link',
                                  attrs: {
                                    href: 'https://www.cinch.co.uk/support',
                                    uuid: null,
                                    anchor: null,
                                    target: '_self',
                                    linktype: 'url',
                                  },
                                },
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                            {
                              text: ' prior to Delivery.',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'We will need a safe place to park the Car (for example, a driveway or safe on-road parking) whilst we complete our delivery checks with you. If you are selling a Part Exchange Car to us, then you should also make sure that is parked safely and is accessible by us. You are responsible for giving us details of a safe location and will be responsible for any costs, penalties or fines we incur as a result of parking in the safe location provided by you.',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'You will own the Car once: (i) you have accepted Delivery of the Car by signing the delivery note; (ii) if you are part exchanging your car with us, we have accepted and received the Part Exchange Car; (please see section 10 for more information); and (iii) we have received full payment of the Purchase Price and any other money you owe to us (including the Admin Fee',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                            {
                              text: ' ',
                              type: 'text',
                            },
                            {
                              text: 'and the Transport Charge',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                            {
                              text: ').',
                              type: 'text',
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'If you are purchasing the Car using a Finance Agreement then the third party finance provider will own the Car once the conditions in section 5.10 have been met.',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'You are responsible for the Car from Delivery.',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'If we attempt to deliver the Car to you and we are unable to do so and we are unable to contact you to rearrange your Delivery, we may treat the Agreement as ended. We will notify you by e-mail that we are treating the Agreement as ended and we will refund any money paid by you to us (including the Admin Fee and any Transport Charge).',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                          ],
                        },
                        {
                          type: 'paragraph',
                        },
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'B: Collection',
                              type: 'text',
                              marks: [
                                {
                                  type: 'bold',
                                },
                              ],
                            },
                          ],
                        },
                        {
                          type: 'paragraph',
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'Provided we receive payment of the applicable collection charge, we ',
                              type: 'text',
                            },
                            {
                              text: 'will hand over the Car to you at the specified collection location on the collection date and time set out in the Order (',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                            {
                              text: 'Collection',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                                {
                                  type: 'bold',
                                },
                              ],
                            },
                            {
                              text: ').',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'We offer different Collection options for your Car:',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                          ],
                        },
                        {
                          type: 'ordered_list',
                          attrs: {
                            order: 1,
                          },
                          content: [
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'where available, Collection from a Store (which includes our Click and Collect locations) ',
                                      type: 'text',
                                    },
                                    {
                                      text: 'or a Marshall dealership ',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                      ],
                                    },
                                    {
                                      text: 'free of charge, we call this “In-Store Collection”; or',
                                      type: 'text',
                                    },
                                  ],
                                },
                              ],
                            },
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'Collection from a WeBuyAnyCar.com branch for a charge, we call this “',
                                      type: 'text',
                                    },
                                    {
                                      text: 'WBAC Collection',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'bold',
                                        },
                                      ],
                                    },
                                    {
                                      text: '”. The charge payable by you for a WBAC Collection will be set out on the Site at the time you place your Order. This Collection charge is an example of a Transport Charge in these Terms. ',
                                      type: 'text',
                                    },
                                  ],
                                },
                                {
                                  type: 'paragraph',
                                },
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'Our Collection charges can change from time to time so please ensure you check what charge will apply each time you look to buy a Car from cinch.',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                      ],
                                    },
                                  ],
                                },
                              ],
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'You may change the time and date of Collection by calling our customer experience team whose number can be found ',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                            {
                              text: 'here',
                              type: 'text',
                              marks: [
                                {
                                  type: 'link',
                                  attrs: {
                                    href: 'https://www.cinch.co.uk/support',
                                    uuid: null,
                                    anchor: null,
                                    target: '_self',
                                    linktype: 'url',
                                  },
                                },
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                            {
                              text: ' ',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                                {
                                  type: 'bold',
                                },
                              ],
                            },
                            {
                              text: 'at any time before 12pm on the day before your agreed Collection date. We will rearrange the date and time of your Collection once at no extra cost to you (except for any additional Transport Charge that may be payable under section 5.4.2 below). However, if you need to rearrange the date and time of your Collection more than once, or you ask to change the date and time of your Collection after 12pm on the day before your agreed Collection date, we may charge you an extra administration fee of £150.',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'If you choose Collection when you place your Order but later change your mind and want your Car delivered or need to move your agreed Collection location, time or date, and this change means an alternative collection or delivery option will apply instead (for example because your Collection option moves from In-Store Collection to WBAC Collection, where available), then:',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                          ],
                        },
                        {
                          type: 'ordered_list',
                          attrs: {
                            order: 1,
                          },
                          content: [
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'if the Transport Charge for the new option is lower than your original choice, we will refund the difference in the Transport Charge you have already paid compared to the Transport Charge of the new option; or',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                      ],
                                    },
                                  ],
                                },
                              ],
                            },
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'if the Transport charge for the new option is higher than your original choice, we reserve the right to charge you for the difference in the Transport Charge.',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                      ],
                                    },
                                  ],
                                },
                                {
                                  type: 'paragraph',
                                },
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'See section 5.2 for more information on delivery options. Please note, if your Car is available for Collection at a Marshall dealership and you opt to collect the Car from this location at the point of Order we cannot later move the Car to a different location for Collection. In this case, should you no longer wish to collect from the Marshall dealership, we can deliver your Car to you instead and Part A of this section (and a Transport Charge) will then apply.',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                      ],
                                    },
                                  ],
                                },
                              ],
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'You will only be able to collect the Car from the Collection location set out in the Order (or the alternative location we agree in accordance with these Terms). The person stated on the Order must be present at Collection as we will not hand over the Car to any other person.',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'At Collection, you must show us either:  ',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                          ],
                        },
                        {
                          type: 'ordered_list',
                          attrs: {
                            order: 1,
                          },
                          content: [
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'a valid photocard UK driving licence and, if the address on your driving licence is different to your residential address stated on the Order, a utility bill or bank statement addressed to your residential address and which is dated in the three months prior to Collection; or',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                      ],
                                    },
                                  ],
                                },
                              ],
                            },
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'your passport and a utility bill or bank statement addressed to your residential address and which is dated in the three months prior to Collection.',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                      ],
                                    },
                                  ],
                                },
                              ],
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'We may also request that you to show us:',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                          ],
                        },
                        {
                          type: 'ordered_list',
                          attrs: {
                            order: 1,
                          },
                          content: [
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'the payment card(s) used to pay for your Car; or',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                      ],
                                    },
                                  ],
                                },
                              ],
                            },
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'if your Part Exchange Car is of a higher value than the Purchase Price of the Car, a payment card, bank statement or other document provided by your bank that contains the bank account information you have provided to us in order for us to pay you the Extra Value (see Part D of section 10 for more information).',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                      ],
                                    },
                                  ],
                                },
                              ],
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'Please ensure that you bring the correct identification documents and payment card(s) (if applicable) to your collection appointment as we will not be able to hand the Car over to you without these. If you have any questions on what identification documents you will require please call our customer experience team whose number can be found ',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                            {
                              text: 'here',
                              type: 'text',
                              marks: [
                                {
                                  type: 'link',
                                  attrs: {
                                    href: 'https://www.cinch.co.uk/support',
                                    uuid: null,
                                    anchor: null,
                                    target: '_self',
                                    linktype: 'url',
                                  },
                                },
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                            {
                              text: ' prior to Collection.',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'You will own the Car once (i) you have accepted Collection of the Car by signing the delivery note; (ii) if you are part exchanging your car with us, we have accepted and received the Part Exchange Car (please see section 10 for more information); and (iii) we have received full payment of the Purchase Price and any other money you owe to us (including the Admin Fee and the Transport Charge, if applicable).',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'If you are purchasing the Car using a Finance Agreement then the third party finance provider will own the Car once the conditions in section 5.22 have been met.',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'You are responsible for the Car from Collection.',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'If you fail to collect the Car from us on your arranged Collection date and we are unable to contact you regarding your Collection to rearrange your Collection appointment, we may treat the Agreement as ended. We will notify you by e-mail that we are treating the Agreement as ended and we will refund any money paid by you to us (including the Admin Fee and any Transport Charge).',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                          ],
                        },
                      ],
                    },
                  ],
                },
              ],
            },
            {
              type: 'list_item',
              content: [
                {
                  type: 'paragraph',
                  content: [
                    {
                      text: 'Payment',
                      type: 'text',
                      marks: [
                        {
                          type: 'bold',
                        },
                      ],
                    },
                  ],
                },
                {
                  type: 'paragraph',
                  content: [
                    {
                      text: 'A: Price and payment methods',
                      type: 'text',
                      marks: [
                        {
                          type: 'bold',
                        },
                        {
                          type: 'styled',
                          attrs: {
                            class: '',
                          },
                        },
                      ],
                    },
                  ],
                },
                {
                  type: 'ordered_list',
                  attrs: {
                    order: 1,
                  },
                  content: [
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'The purchase price for the Car is the price stated when you placed your Order on cinch (',
                              type: 'text',
                            },
                            {
                              text: 'Purchase Price',
                              type: 'text',
                              marks: [
                                {
                                  type: 'bold',
                                },
                              ],
                            },
                            {
                              text: '). The price stated includes VAT.',
                              type: 'text',
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                        },
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'Separately to the Purchase Price:',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                          ],
                        },
                        {
                          type: 'ordered_list',
                          attrs: {
                            order: 1,
                          },
                          content: [
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'an administration fee in the sum of £99 will apply to your Order as a contribution towards our administration costs relating to the processing of your Order and provision of a faff-free customer experience (Admin Fee). The Admin Fee includes VAT; and',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                      ],
                                    },
                                  ],
                                },
                              ],
                            },
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'a Transport Charge will be due unless your Car is made available through an In-Store Collection. The Transport Charge includes VAT.',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                      ],
                                    },
                                  ],
                                },
                              ],
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'The Purchase Price, the Admin Fee, the Transport Charge (where due) and the amount payable for any other optional products relating to the purchase of your Car (for example, cinchShield) will be payable when you place your Order. If you have chosen to pay for the Car using a Finance Agreement with a Lender (as defined in section 11 below), you must pay the deposit set out on your Order when you place your Order, together with your Admin Fee, the Transport Charge (where due) and the amount payable for any other optional products relating to the purchase of your Car. Please note, finance is designed to cover the Purchase Price of your Car only and will not cover the deposit, Admin Fee, ',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                            {
                              text: 'Transport Charge',
                              type: 'text',
                            },
                            {
                              text: ' or the cost of optional products you chose to add to your purchase. We may accept the value of your Part Exchange Car (if applicable) as payment of, or towards, your deposit.',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'You may make payments to us by Visa or Mastercard debit or credit cards (either directly or using a mobile payment system such as Apple Pay or Google Pay), by open banking payment (Pay by Bank) or, in respect of the Purchase Price only, by arranging finance from a Lender in relation to the purchase of your Car. We do not accept any other form of payment. If you are paying by debit or credit card, your payment card must be linked to a bank account or credit card in your name. We do not accept payment cards that have been supplied or funded directly by third party car finance providers (including prepaid cards).',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'If you choose to purchase one of our cinchCover products or GAP insurance, you will need to set up a direct debit with the administrator of the product for the payment of the monthly premium. Please see sections 8.4 and 8.26 for more information. ',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'We use a third-party payment provider to process card and Pay by Bank payments from you to us on the Site. The processing of these payments will be governed by the third-party provider’s terms and conditions. You can find and read their terms ',
                              type: 'text',
                            },
                            {
                              text: 'here',
                              type: 'text',
                              marks: [
                                {
                                  type: 'link',
                                  attrs: {
                                    href: 'https://stripe.com/gb/legal/link-account-terms',
                                    uuid: null,
                                    anchor: null,
                                    target: '_self',
                                    linktype: 'url',
                                  },
                                },
                              ],
                            },
                            {
                              text: '.',
                              type: 'text',
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'You are responsible for the payment of vehicle tax. The V5C reference number will be provided to you prior to Delivery or Collection so that you can arrange vehicle tax for the Car with the DVLA to start on Delivery or Collection. We will notify the DVLA of the change in ownership',
                              type: 'text',
                            },
                          ],
                        },
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'B: Promotions',
                              type: 'text',
                              marks: [
                                {
                                  type: 'bold',
                                },
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'We may sometimes offer a price reduction on certain used cars listed on the Site. Qualifying used cars and the amount of price reduction will be identified by relevant signposting on the Site, including on the individual car’s details page. The Purchase Price of the Car (including for the purposes of calculating any refund due to you under section 12) will be the price paid for the Car after deduction of the specified price reduction, not including the Admin Fee or any Transport Charge.',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'We may sometimes offer a deposit contribution on certain used cars listed on the Site when you choose to pay for the Car using a Finance Agreement with a Lender. This means that we will make a contribution to your finance deposit for the Car. Qualifying cars and the amount of the deposit contribution is specified on the Site. Deposit contributions are not available on purchases paid for by card payment or Pay by Bank.',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'Promotions will be offered on selected used cars only at our discretion. We reserve the right to cancel, amend, withdraw, terminate or suspend a promotional offer at any time. Offers cannot be used in conjunction with any other offers unless otherwise stated and are not transferable. No cash alternative is available. Certain promotions may have additional terms and conditions which will also apply and these will be specified on the Site or in communications issued in respect of the promotion, where applicable. ',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                          ],
                        },
                        {
                          type: 'paragraph',
                        },
                      ],
                    },
                  ],
                },
              ],
            },
            {
              type: 'list_item',
              content: [
                {
                  type: 'paragraph',
                  content: [
                    {
                      text: 'Your Car',
                      type: 'text',
                      marks: [
                        {
                          type: 'bold',
                        },
                      ],
                    },
                  ],
                },
                {
                  type: 'ordered_list',
                  attrs: {
                    order: 1,
                  },
                  content: [
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'The Car may have been used previously for lease or rental and may have had multiple users. It may also have been imported from the Republic of Ireland.',
                              type: 'text',
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'Each Car undergoes a thorough inspection prior to being advertised for sale. Please check our ',
                              type: 'text',
                            },
                            {
                              text: 'Quality standards',
                              type: 'text',
                              marks: [
                                {
                                  type: 'link',
                                  attrs: {
                                    href: 'https://www.cinch.co.uk/car-inspection',
                                    uuid: null,
                                    anchor: null,
                                    target: '_self',
                                    linktype: 'url',
                                  },
                                },
                              ],
                            },
                            {
                              text: ' page for more information in relation to the inspection. As part of the inspection, we check each Car for a manufacturer recall notice. We do not give or make any representation, warranty or undertaking that the Car will not be subject to a manufacturer recall notice at the point of sale or at any time in the future.',
                              type: 'text',
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'The images of Cars on cinch are high resolution images. We make every effort to display the Car accurately, including damage (which is beyond the ordinary wear and tear of a used car), but we cannot guarantee that the pictures of the Car on cinch show every part of the Car or colour of the Car.  We will not be responsible if the images of the Car do not display correctly on the device on which you view them. If you are not satisfied with the actual appearance of your Car you may cancel the purchase using our 14 day money back guarantee (see section 12 for more information).',
                              type: 'text',
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'The mileage of a Car stated on cinch may vary slightly to the actual mileage of the Car because of delivering the Car to you. The increase in mileage will be no more than 200 miles.',
                              type: 'text',
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'Please contact us before placing the Order to ask any questions you have about the Car. You can find our contact details ',
                              type: 'text',
                            },
                            {
                              text: 'here',
                              type: 'text',
                              marks: [
                                {
                                  type: 'link',
                                  attrs: {
                                    href: 'https://www.cinch.co.uk/support',
                                    uuid: null,
                                    anchor: null,
                                    target: '_self',
                                    linktype: 'url',
                                  },
                                },
                              ],
                            },
                            {
                              text: '.',
                              type: 'text',
                            },
                          ],
                        },
                      ],
                    },
                  ],
                },
              ],
            },
            {
              type: 'list_item',
              content: [
                {
                  type: 'paragraph',
                  content: [
                    {
                      text: 'Car care and cinchCharge',
                      type: 'text',
                      marks: [
                        {
                          type: 'bold',
                        },
                      ],
                    },
                  ],
                },
                {
                  type: 'paragraph',
                  content: [
                    {
                      text: 'A: cinchCover',
                      type: 'text',
                      marks: [
                        {
                          type: 'bold',
                        },
                      ],
                    },
                  ],
                },
                {
                  type: 'ordered_list',
                  attrs: {
                    order: 1,
                  },
                  content: [
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'cinch has partnered with a third party insurer (',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                            {
                              text: 'Assurant',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                                {
                                  type: 'bold',
                                },
                              ],
                            },
                            {
                              text: ') to offer cinchCover, optional car care products available to purchase through cinch. Assurant is a trading name of both London General Insurance Company Limited (',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                            {
                              text: 'LGI',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                                {
                                  type: 'bold',
                                },
                              ],
                            },
                            {
                              text: ', the insurer of cinchCover) and TWG Services Limited (',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                            {
                              text: 'TWG',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                                {
                                  type: 'bold',
                                },
                              ],
                            },
                            {
                              text: ', the administrator of cinchCover):',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                          ],
                        },
                        {
                          type: 'ordered_list',
                          attrs: {
                            order: 1,
                          },
                          content: [
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'LGI is authorised by the Prudential Regulation Authority (',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                      ],
                                    },
                                    {
                                      text: 'PRA',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                        {
                                          type: 'bold',
                                        },
                                      ],
                                    },
                                    {
                                      text: ') and regulated by the Financial Conduct Authority (',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                      ],
                                    },
                                    {
                                      text: 'FCA',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                        {
                                          type: 'bold',
                                        },
                                      ],
                                    },
                                    {
                                      text: ') and the PRA (Financial Services Register No. 202689); and',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                      ],
                                    },
                                  ],
                                },
                              ],
                            },
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'TWG is authorised and regulated by the FCA (Financial Services Register No. 312440).',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                      ],
                                    },
                                  ],
                                },
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'You can find out more information about Assurant and cinchCover ',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                      ],
                                    },
                                    {
                                      text: 'here',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'link',
                                          attrs: {
                                            href: 'https://www.cinch.co.uk/car-care/cinch-cover',
                                            uuid: null,
                                            anchor: null,
                                            target: '_self',
                                            linktype: 'url',
                                          },
                                        },
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                      ],
                                    },
                                    {
                                      text: '.',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                      ],
                                    },
                                  ],
                                },
                              ],
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'When you buy a Car on cinch, you can choose to purchase one or more of our cinchCover products:',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                          ],
                        },
                        {
                          type: 'ordered_list',
                          attrs: {
                            order: 1,
                          },
                          content: [
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'Extended warranty and RAC breakdown assistance;',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                      ],
                                    },
                                  ],
                                },
                              ],
                            },
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'Scratch and dent insurance;',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                      ],
                                    },
                                  ],
                                },
                              ],
                            },
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'Alloy wheel insurance; and',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                      ],
                                    },
                                  ],
                                },
                              ],
                            },
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'Tyre insurance.',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                      ],
                                    },
                                  ],
                                },
                              ],
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'cinchCover and any other service provided by Assurant is subject to a contract between you and Assurant on the terms set out in the policy wording documentation provided to you in the confirmation email you will receive from Assurant. We are not responsible for providing cinchCover to you if Assurant fails to do so however you will have the protection of the Financial Services Compensation Scheme (more information can be found ',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                            {
                              text: 'here',
                              type: 'text',
                              marks: [
                                {
                                  type: 'link',
                                  attrs: {
                                    href: 'https://www.fscs.org.uk/',
                                    uuid: null,
                                    anchor: null,
                                    target: '_self',
                                    linktype: 'url',
                                  },
                                },
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                            {
                              text: ').  ',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'If you choose to purchase one of our cinchCover products, you will need to set up a direct debit with TWG for payment of the monthly premium which will be collected in 10 monthly instalments by TWG. Payment for cinchCover must be made from a UK bank account in your name. If the name on your Order (and therefore the policyholder under cinchCover) is different to your UK registered bank account, please contact the TWG customer experience team to discuss your options on 0333 220 6622 (Monday to Friday 9:00am to 5:00pm) or by email ',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                            {
                              text: 'cinchcover@assurant.com',
                              type: 'text',
                              marks: [
                                {
                                  type: 'link',
                                  attrs: {
                                    href: 'mailto:cinchcover@assurant.com',
                                    uuid: null,
                                    anchor: null,
                                    target: '_self',
                                    linktype: 'url',
                                  },
                                },
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                            {
                              text: '. For example, if your Order is in your married name but your bank account is in your maiden name, TWG may ask you to provide a copy of your marriage certificate.',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'If you purchase a cinchCover product through cinch we may receive a payment or other benefit from Assurant. This payment is typically a fixed fee or fixed percentage of the retail price of the product. The payment we receive may vary between cinchCover products. This payment will not change the advertised price of the product you agree to pay at purchase.',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                          ],
                        },
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'B: 90-day Warranty',
                              type: 'text',
                              marks: [
                                {
                                  type: 'bold',
                                },
                              ],
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'When you buy a Car from cinch',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                            {
                              text: ', you will automatically be opted in for a 90-day warranty with breakdown assistance for the Car at no additional cost to you. We are the provider of the 90-day warranty.',
                              type: 'text',
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: ' The 90-day warranty with breakdown assistance will commence on Delivery or Collection. If you do not wish to receive the 90-day warranty with breakdown assistance, you may cancel this benefit by notifying our customer experience team (whose number can be found ',
                              type: 'text',
                            },
                            {
                              text: 'here',
                              type: 'text',
                              marks: [
                                {
                                  type: 'link',
                                  attrs: {
                                    href: 'https://www.cinch.co.uk/support',
                                    uuid: null,
                                    anchor: null,
                                    target: '_self',
                                    linktype: 'url',
                                  },
                                },
                              ],
                            },
                            {
                              text: ').',
                              type: 'text',
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'The 90-day warranty is subject to the terms and conditions for the 90-day warranty that are provided to you in the confirmation email you will receive from us after you have placed your Order for the Car. You should make sure that you read these terms and conditions to understand your responsibilities under the 90-day warranty (so you can access the benefits it provides) and the exclusions that apply to the 90-day warranty.  We are responsible for providing the 90-day warranty to you in accordance with the terms and conditions of the 90-day warranty. You have various legal rights and remedies available to you as a consumer if we do supply the Car to you in accordance with these Terms and your legal rights in respect of faulty goods. None of these rights or remedies are affected by the terms of the 90-day warranty.  ',
                              type: 'text',
                            },
                          ],
                        },
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'C: Manufacturer warranty',
                              type: 'text',
                              marks: [
                                {
                                  type: 'bold',
                                },
                              ],
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'We do not provide a manufacturer’s warranty or guarantee in relation to the Car. Any manufacturer warranty or guarantee that may have existed previously in relation to the Car may no longer be valid at the time you place your Order with us. For example, a previous owner may not have serviced the Car at a main dealer where required by the terms of the original manufacturer warranty or guarantee.',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'If we hold any supporting evidence (such as previous service history for the Car) that may assist you in demonstrating that a manufacturer warranty or guarantee is still valid, we will use our reasonable efforts to pass this to you.',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'We will not be responsible to you if any manufacturer warranty or guarantee in respect of the Car is invalid.',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                          ],
                        },
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'D: cinchShield',
                              type: 'text',
                              marks: [
                                {
                                  type: 'bold',
                                },
                              ],
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'cinchShield is an optional paint, glass and interior protection product offered by cinch in partnership with Altro Limited (which trades as Autoglym) (',
                              type: 'text',
                            },
                            {
                              text: 'Autoglym',
                              type: 'text',
                              marks: [
                                {
                                  type: 'bold',
                                },
                              ],
                            },
                            {
                              text: ').  When you place an Order for a Car you may have the option to purchase cinchShield which will be applied to your Car before Delivery or Collection.',
                              type: 'text',
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'When you choose to add cinchShield to your Order you will also receive the benefit of the LifeShine Guarantee provided by Autoglym.  You will have a legally binding contract with Autoglym in relation to the LifeShine Guarantee.  You can find the terms of this contract ',
                              type: 'text',
                            },
                            {
                              text: 'here',
                              type: 'text',
                              marks: [
                                {
                                  type: 'link',
                                  attrs: {
                                    href: 'https://www.cinch.co.uk/cinchshield-lifeshine-guarantee.pdf',
                                    uuid: null,
                                    anchor: null,
                                    target: '_self',
                                    linktype: 'url',
                                  },
                                },
                              ],
                            },
                            {
                              text: '. You should make sure that you read these terms to understand the exclusions that apply to the LifeShine Guarantee. ',
                              type: 'text',
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'It is essential that you register for the LifeShine Guarantee with Autoglym within 30 days of Delivery or Collection of the Car.  If you do not do so then the LifeShine Guarantee will not be valid. Autoglym will not contact you following Delivery or Collection of the Car until you have registered for the LifeShine Guarantee.',
                              type: 'text',
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'The paint protection element of cinchShield cannot be applied to paintwork on cars with a matte paint finish or to the soft top roof area of a car with that feature.',
                              type: 'text',
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'We will apply cinchShield to the Car once you have placed your Order and we cannot then remove it.  This means that you cannot cancel your order of cinchShield.  You can return the Car under the 14 day money back guarantee and we will repay to you the amount you paid for cinchShield together with the Purchase Price, Admin Fee ',
                              type: 'text',
                            },
                            {
                              text: 'and any Transport Charge you’ve paid ',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                            {
                              text: 'in accordance with section 12.6. ',
                              type: 'text',
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: ' If you have any questions about the LifeShine Guarantee you should contact Autoglym using the contact details at lifeshine.com/contact-us.',
                              type: 'text',
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: ' You may pay for cinchShield using:',
                              type: 'text',
                            },
                          ],
                        },
                        {
                          type: 'ordered_list',
                          attrs: {
                            order: 1,
                          },
                          content: [
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: ' Visa or Mastercard debit or credit cards ',
                                      type: 'text',
                                    },
                                    {
                                      text: '(either directly or through a mobile payment system)',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                      ],
                                    },
                                    {
                                      text: ';',
                                      type: 'text',
                                    },
                                  ],
                                },
                              ],
                            },
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: ' or by Pay by Bank; or',
                                      type: 'text',
                                    },
                                  ],
                                },
                              ],
                            },
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'some or all of the value of your Part Exchange Car, provided the combined total of the Purchase Price of the Car and the Admin Fee is less than the value of your Part Exchange Car (see Part D of section 10 below for more information).',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                      ],
                                    },
                                  ],
                                },
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'We do not accept any other form of payment for cinchShield',
                                      type: 'text',
                                    },
                                    {
                                      text: ', including payment cards that have been supplied or funded by third party car finance providers (including prepaid cards)',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                      ],
                                    },
                                    {
                                      text: '.',
                                      type: 'text',
                                    },
                                  ],
                                },
                              ],
                            },
                          ],
                        },
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'E: cinchCharge',
                              type: 'text',
                              marks: [
                                {
                                  type: 'bold',
                                },
                              ],
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'cinch has partnered with a third-party provider of a public charging network for electric vehicles, Paua Tech Limited (',
                              type: 'text',
                            },
                            {
                              text: 'Paua',
                              type: 'text',
                              marks: [
                                {
                                  type: 'bold',
                                },
                              ],
                            },
                            {
                              text: '), to offer access via a physical card and mobile application to that charging network (',
                              type: 'text',
                            },
                            {
                              text: 'cinchCharge',
                              type: 'text',
                              marks: [
                                {
                                  type: 'bold',
                                },
                              ],
                            },
                            {
                              text: '). You can find out more about cinchCharge and Paua ',
                              type: 'text',
                            },
                            {
                              text: 'here',
                              type: 'text',
                              marks: [
                                {
                                  type: 'link',
                                  attrs: {
                                    href: 'https://cinch.co.uk/car-care/charging-electric-cars',
                                    uuid: null,
                                    anchor: null,
                                    target: '_self',
                                    linktype: 'url',
                                  },
                                },
                                {
                                  type: 'underline',
                                },
                              ],
                            },
                            {
                              text: '. ',
                              type: 'text',
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'When you buy a fully electric Car on cinch, you will also receive the benefit of being able to access cinchCharge provided by Paua. If you choose to register for cinchCharge, you will enter into a direct contract with Paua for your use of the cinchCharge service (including the card and mobile application) and any other services provided to you by Paua. You can find the terms of this contract ',
                              type: 'text',
                            },
                            {
                              text: 'here',
                              type: 'text',
                              marks: [
                                {
                                  type: 'link',
                                  attrs: {
                                    href: 'https://app.pauatech.com/PauaFleet/Paua-Driver-terms-7-12-22.pdf',
                                    uuid: null,
                                    anchor: null,
                                    target: '_self',
                                    linktype: 'url',
                                  },
                                },
                                {
                                  type: 'underline',
                                },
                              ],
                            },
                            {
                              text: '. We are not responsible for providing cinchCharge to you if Paua fails to do so.',
                              type: 'text',
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'If your Order is cancelled prior to Delivery or Collection or if you return the Car to us under the 14-day money-back guarantee (or for any other reason), you will no longer be able to access cinchCharge and, if you have registered a cinchCharge account with Paua, it will be terminated in accordance with the terms of your contract with Paua. ',
                              type: 'text',
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'If you have any questions about cinchCharge, you should contact Paua using the contact details at ',
                              type: 'text',
                            },
                            {
                              text: 'pauatech.com/contact-us',
                              type: 'text',
                              marks: [
                                {
                                  type: 'link',
                                  attrs: {
                                    href: 'https://www.pauatech.com/contact-us',
                                    uuid: null,
                                    anchor: null,
                                    target: '_self',
                                    linktype: 'url',
                                  },
                                },
                                {
                                  type: 'underline',
                                },
                              ],
                            },
                            {
                              text: '.',
                              type: 'text',
                            },
                          ],
                        },
                        {
                          type: 'paragraph',
                        },
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'F: GAP insurance',
                              type: 'text',
                              marks: [
                                {
                                  type: 'bold',
                                },
                              ],
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'cinch has partnered with a third party insurer and administrator to offer GAP insurance, an optional insurance product available to purchase through cinch. GAP insurance (also known as Asset Protection Insurance) is marketed under the “Autotrust” brand. Motors Insurance Company Limited (',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                            {
                              text: 'MICL',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                                {
                                  type: 'bold',
                                },
                              ],
                            },
                            {
                              text: ') is the insurer of this GAP Insurance product and Car Care Plan Limited (',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                            {
                              text: 'CCP',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                                {
                                  type: 'bold',
                                },
                              ],
                            },
                            {
                              text: ') is the administrator:',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                          ],
                        },
                        {
                          type: 'ordered_list',
                          attrs: {
                            order: 1,
                          },
                          content: [
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'MICL is authorised by the Prudential Regulation Authority (',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                      ],
                                    },
                                    {
                                      text: 'PRA',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                        {
                                          type: 'bold',
                                        },
                                      ],
                                    },
                                    {
                                      text: ') and regulated by the Financial Conduct Authority (',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                      ],
                                    },
                                    {
                                      text: 'FCA',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                        {
                                          type: 'bold',
                                        },
                                      ],
                                    },
                                    {
                                      text: ') and the PRA (Financial Services Register No. 202875); and',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                      ],
                                    },
                                  ],
                                },
                              ],
                            },
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'CCP is authorised and regulated by the FCA (Financial Services Register No.309268).',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                      ],
                                    },
                                  ],
                                },
                                {
                                  type: 'paragraph',
                                },
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'You can find out more information about GAP Insurance ',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                      ],
                                    },
                                    {
                                      text: 'here',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'link',
                                          attrs: {
                                            href: 'https://www.cinch.co.uk/car-care/gap-insurance',
                                            uuid: null,
                                            anchor: null,
                                            target: '_self',
                                            linktype: 'url',
                                          },
                                        },
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                      ],
                                    },
                                    {
                                      text: '.',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                      ],
                                    },
                                  ],
                                },
                              ],
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'If you choose to purchase GAP Insurance for your Car through cinch:',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                          ],
                        },
                        {
                          type: 'ordered_list',
                          attrs: {
                            order: 1,
                          },
                          content: [
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'you will enter into a separate contract for GAP Insurance between you, MICL and CCP on the terms set out in the policy wording documentation we provide to you and distributed in the welcome email you will receive from CCP. Please read the policy wording documentation carefully as it sets out the terms and conditions of your GAP insurance cover, the limitations of the cover, your rights and your responsibilities; and',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                      ],
                                    },
                                  ],
                                },
                              ],
                            },
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'you will need to set up a direct debit with CCP for payment of the monthly premium which will be collected in 10 monthly instalments. Payment for GAP Insurance must be made from a UK bank account in your name.',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                      ],
                                    },
                                  ],
                                },
                              ],
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'Please speak to the CCP customer experience team where:',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                          ],
                        },
                        {
                          type: 'ordered_list',
                          attrs: {
                            order: 1,
                          },
                          content: [
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'you do not meet the eligibility cover for GAP Insurance; or ',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                      ],
                                    },
                                  ],
                                },
                              ],
                            },
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'the name on your Order (and therefore the policyholder under GAP Insurance) is different to your UK registered bank account.',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                      ],
                                    },
                                  ],
                                },
                                {
                                  type: 'paragraph',
                                },
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'The CCP customer experience team is available on 0344 573 8069 (Monday to Friday 8:30am to 5:30pm and Saturday 9.00am to 12.30 pm). If your Order is in your married name but your bank account is in your maiden name, CCP may ask you to provide a copy of your marriage certificate.',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                      ],
                                    },
                                  ],
                                },
                              ],
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'We are not responsible for providing GAP Insurance to you if MICL or CCP fail to do so however you will have the protection of the Financial Services Compensation Scheme (more information can be found ',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                            {
                              text: 'here',
                              type: 'text',
                              marks: [
                                {
                                  type: 'link',
                                  attrs: {
                                    href: 'https://www.cinch.co.uk/car-care/gap-insurance',
                                    uuid: null,
                                    anchor: null,
                                    target: '_self',
                                    linktype: 'url',
                                  },
                                },
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                            {
                              text: ')',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'If you purchase GAP Insurance we may receive a payment or other benefit from CCP. The amount of the payment we receive depends on the Purchase Price of your Car. This payment does not change the advertised price of the product you agree to pay at purchase.',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                          ],
                        },
                      ],
                    },
                  ],
                },
              ],
            },
            {
              type: 'list_item',
              content: [
                {
                  type: 'paragraph',
                  content: [
                    {
                      text: 'Insurance to drive your Car',
                      type: 'text',
                      marks: [
                        {
                          type: 'bold',
                        },
                      ],
                    },
                  ],
                },
                {
                  type: 'ordered_list',
                  attrs: {
                    order: 1,
                  },
                  content: [
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'You will be required to provide your own insurance for the Car from the date of Delivery or Collection in order to drive the Car lawfully. If you opt to purchase GAP Insurance the eligibility requirements for that product will stipulate what level of motor insurance you will need to have in place.  ',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'We accept no responsibility to procure the provision of any driving insurance services to you. ',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                          ],
                        },
                      ],
                    },
                  ],
                },
              ],
            },
            {
              type: 'list_item',
              content: [
                {
                  type: 'paragraph',
                  content: [
                    {
                      text: ' Part exchange',
                      type: 'text',
                      marks: [
                        {
                          type: 'bold',
                        },
                      ],
                    },
                  ],
                },
                {
                  type: 'paragraph',
                  content: [
                    {
                      text: 'A: General Terms',
                      type: 'text',
                      marks: [
                        {
                          type: 'bold',
                        },
                      ],
                    },
                  ],
                },
                {
                  type: 'ordered_list',
                  attrs: {
                    order: 1,
                  },
                  content: [
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'Where you choose to use a Part Exchange Car in partial or full payment of the Purchase Price, you must provide details of the Part Exchange Car as part of your Order including whether the Part Exchange Car is subject to an existing finance agreement. We may ask you for additional information about the Part Exchange Car at any time prior to Delivery or Collection.',
                              type: 'text',
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'We will give you a valuation for the Part Exchange Car when you place your Order. You must provide true, accurate and up-to-date information to us about the Part Exchange Car. The Part Exchange Car may be inspected on Delivery or Collection to ensure the information provided when you placed your Order was true and accurate. We may also need to ensure that the Part Exchange Car is roadworthy and has not sustained any damage you have not disclosed to us.',
                              type: 'text',
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'We may reject a Part Exchange Car at any time if:',
                              type: 'text',
                            },
                          ],
                        },
                        {
                          type: 'ordered_list',
                          attrs: {
                            order: 1,
                          },
                          content: [
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'you are not legally capable of entering into a binding contract to sell the Part Exchange Car;',
                                      type: 'text',
                                    },
                                  ],
                                },
                              ],
                            },
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'you are not the sole owner of the Part Exchange Car (including, if relevant, the electric car battery);',
                                      type: 'text',
                                    },
                                  ],
                                },
                              ],
                            },
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'another person has any claim to the Part Exchange Car or there is outstanding finance on the Part Exchange Car which you have not told us about as part of the order process;',
                                      type: 'text',
                                    },
                                  ],
                                },
                              ],
                            },
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'you are not named as the registered keeper of the Part Exchange Car on the V5C registration document;',
                                      type: 'text',
                                    },
                                  ],
                                },
                              ],
                            },
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'you have owned the Part Exchange Car for less than 90 days;',
                                      type: 'text',
                                    },
                                  ],
                                },
                              ],
                            },
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'any information given to us about the Part Exchange Car is not true and accurate;',
                                      type: 'text',
                                    },
                                  ],
                                },
                              ],
                            },
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'the Part Exchange Car has sustained damage that is not disclosed to us when you placed the Order;',
                                      type: 'text',
                                    },
                                  ],
                                },
                              ],
                            },
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'there is a material discrepancy in the information and documents supplied by you in respect of the Part Exchange Car or a material document is missing, inaccurate or incomplete (for example, the V5C registration document is missing or incomplete);',
                                      type: 'text',
                                    },
                                  ],
                                },
                              ],
                            },
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'the Part Exchange Car is not roadworthy or has any fault which may affect the value of the Part Exchange Car;',
                                      type: 'text',
                                    },
                                  ],
                                },
                              ],
                            },
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'the Part Exchange Car is not registered in the UK or is an import (that is, it is not a right-hand drive car with UK registration plates or was not first registered in the UK);',
                                      type: 'text',
                                    },
                                  ],
                                },
                              ],
                            },
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'there is a finance marker on the Part Exchange Car or any other issue which you have not told us about as part of the order process (identified by any vehicle checks we carry out using industry databases);',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                      ],
                                    },
                                  ],
                                },
                              ],
                            },
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'the Part Exchange Car does not have a valid MOT;',
                                      type: 'text',
                                    },
                                  ],
                                },
                              ],
                            },
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'the Part Exchange Car has at any time been in a major accident or has been subject to a total loss claim;',
                                      type: 'text',
                                    },
                                  ],
                                },
                              ],
                            },
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'the Part Exchange Car does not bear its legal registration and chassis number;',
                                      type: 'text',
                                    },
                                  ],
                                },
                              ],
                            },
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'the Part Exchange Car has been modified for appearance or performance purposes;',
                                      type: 'text',
                                    },
                                  ],
                                },
                              ],
                            },
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'the actual mileage of the Part Exchange Car is more than 1,000 miles above the mileage stated in the Order;',
                                      type: 'text',
                                    },
                                  ],
                                },
                              ],
                            },
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'the Part Exchange Car has been used for commercial purposes, as a taxi, for private hire or rental, for driving tuition, as an emergency services vehicle or by a local authority;',
                                      type: 'text',
                                    },
                                  ],
                                },
                              ],
                            },
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'we have reasonable grounds to suspect fraud is taking place; or',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                      ],
                                    },
                                  ],
                                },
                              ],
                            },
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'we discover or have reasonable grounds to suspect the Part Exchange Car is a clone or has been previously reported as stolen.  ',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                      ],
                                    },
                                  ],
                                },
                              ],
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'Where the circumstances detailed in section 10.3 are discovered either before or after Delivery or Collection (as applicable) the remedies at Part F of this section 10 will apply.',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                          ],
                        },
                        {
                          type: 'paragraph',
                        },
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'B: Part Exchange - outstanding finance',
                              type: 'text',
                              marks: [
                                {
                                  type: 'bold',
                                },
                              ],
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'If the Part Exchange Car is subject to a finance agreement (which does not include a personal loan or other finance arrangement not linked to, or secured against, the Part Exchange Car), we may be able to settle the outstanding finance on the Part Exchange Car. If you would like us to settle the outstanding finance, you must provide us with the exact amount of the outstanding balance of your finance as set out on the settlement letter when we ask you to do so. It is important that the outstanding balance which you give us is accurate and up-to-date; if the outstanding balance is not accurate or up-to-date then we may cancel your Order (including your purchase of the Car from us and our purchase of the Part Exchange Car from you) in accordance with section 4.4.5 above or, if you have not yet placed your Order, make changes to our offer to purchase the Part Exchange Car from you. Please contact us if you have any questions about how to obtain your outstanding balance.',
                              type: 'text',
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: "We will run some checks on the Part Exchange Car and your outstanding finance. These checks will tell us (i) who owns the Part Exchange Car and who has any rights or interest in it and (ii) whether our valuation of the Part Exchange Car is more or less than the outstanding balance of the finance agreement on the Part Exchange Car (this is often referred to as the Part Exchange Car being in 'positive equity' or 'negative equity').",
                              type: 'text',
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'If the Part Exchange Car is in negative equity (because the outstanding balance of the finance agreement is more than our valuation of the Part Exchange Car), you will be required to pay us the difference between the outstanding finance and our valuation at the time you place your Order. This payment can be made by Visa or Mastercard debit or credit cards (either directly or through a mobile payment system) or using Pay by Bank. We do not accept any other form of payment, including payment cards that have been supplied or funded by third party car finance providers (including prepaid cards).',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'You must provide the settlement letter to us promptly when we request it in accordance with our instructions to you.  The settlement letter must be valid for at least 14 days after you place your Order. The settlement letter must meet the requirements set out on the Site and in any communications with you.',
                              type: 'text',
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'When we agree to settle the outstanding finance on the Part Exchange Car we have to deliver or collect your Car, collect the Part Exchange Car and have enough time to settle the finance before the date specified on your settlement letter. Due to this time constraint we may not be able to offer you our usual range of Delivery and Collection dates. If the date of Delivery or Collection changes or is delayed for any reason and there are no available dates for Delivery or Collection which would allow us adequate time to settle the outstanding finance on the Part Exchange Car, then we may take the action set out in section 10.10 which may include cancelling your Order.',
                              type: 'text',
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'If:',
                              type: 'text',
                            },
                          ],
                        },
                        {
                          type: 'ordered_list',
                          attrs: {
                            order: 1,
                          },
                          content: [
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'as a result of our checks under section 10.6, we are not able to settle the outstanding finance on the Part Exchange Car; or',
                                      type: 'text',
                                    },
                                  ],
                                },
                              ],
                            },
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'the settlement letter does not meet the requirements set out in section 10.8 ',
                                      type: 'text',
                                    },
                                    {
                                      text: 'including if you do not provide the settlement letter to us with at least 14 days of validity remaining after you place your Order',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                      ],
                                    },
                                    {
                                      text: '; or',
                                      type: 'text',
                                    },
                                  ],
                                },
                              ],
                            },
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'as a result of a change in the date of Delivery or Collection as set out in section 10.9, ',
                                      type: 'text',
                                    },
                                    {
                                      text: 'we have insufficient time to settle the outstanding finance on the Part Exchange Car,',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                      ],
                                    },
                                  ],
                                },
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'then you agree that we have the right to withdraw any offer to buy the Part Exchange Car with immediate effect and/or to indicate a revised valuation at which we will be prepared to purchase the Part Exchange Car and/or cancel your Order (in which case we will reimburse you in accordance with section 10.15 of these Terms).',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                      ],
                                    },
                                  ],
                                },
                              ],
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'We are not responsible for any underpayment or overpayment of the finance agreement in relation to your Part Exchange Car. You must speak to your existing lender with any queries in relation to the existing finance agreement.',
                              type: 'text',
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'In certain circumstances, we may need you to contact the person who provides your existing finance on the Part Exchange Car to help us settle the outstanding balance of your finance agreement. You agree that you will take reasonable steps to help us settle your finance where we ask you to do so.',
                              type: 'text',
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'If, for any reason, after you have handed over the Part Exchange Car to us, the amount required to settle the outstanding balance of the finance agreement on the Part Exchange Car is more than the amount you disclosed to us or there are other finance arrangements which mean we have to pay money to another person (',
                              type: 'text',
                            },
                            {
                              text: 'Shortfall Amount',
                              type: 'text',
                              marks: [
                                {
                                  type: 'bold',
                                },
                              ],
                            },
                            {
                              text: '), then you must pay us the Shortfall Amount within 30 days of us requesting that from you.',
                              type: 'text',
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'If, for any reason, your existing lender does not accept our settlement of the outstanding finance on the Part Exchange Car, then we may ask you to pay us an amount equal to the outstanding balance of the finance to us within 30 days of us requesting that from you.',
                              type: 'text',
                            },
                          ],
                        },
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'C: Part Exchange - at Delivery or Collection',
                              type: 'text',
                              marks: [
                                {
                                  type: 'bold',
                                },
                              ],
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'If you are selling a Part Exchange Car to us, we will inspect the Part Exchange Car at Delivery or on Collection (as applicable). We reserve the right not to accept the Part Exchange Car for any reason, including if the Part Exchange Car is damaged or is not roadworthy (because, for example, it won’t start). If we do not accept the Part Exchange Car, then the Order will be cancelled, and Delivery or Collection will not take place. Any payments you have made for or towards the Car (',
                              type: 'text',
                            },
                            {
                              text: 'including any payment for negative equity as described in section 10.7 above) ',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                            {
                              text: 'will be reimbursed to you within 14 days. We will repay you using the same means of payment as you used to pay us, and we will not charge you any fees in relation to the repayment.',
                              type: 'text',
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'If we accept and collect the Part Exchange Car at Delivery or Collection, you must give us:',
                              type: 'text',
                            },
                          ],
                        },
                        {
                          type: 'ordered_list',
                          attrs: {
                            order: 1,
                          },
                          content: [
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: "all copies of the Part Exchange Car's keys;",
                                      type: 'text',
                                    },
                                  ],
                                },
                              ],
                            },
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: "the Part Exchange Car's most recent V5C registration document (together with appropriate proof of purchase if requested by us);",
                                      type: 'text',
                                    },
                                  ],
                                },
                              ],
                            },
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: "the Part Exchange Car's service history (if any);",
                                      type: 'text',
                                    },
                                  ],
                                },
                              ],
                            },
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: "the Part Exchange Car's MOT certificate (if any);",
                                      type: 'text',
                                    },
                                  ],
                                },
                              ],
                            },
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: "the Part Exchange Car's user manuals (if any); and",
                                      type: 'text',
                                    },
                                  ],
                                },
                              ],
                            },
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'any accessories there may be such as a locking wheel nut, radio fascia, remote controls, SD card and/or charging cables (if you have a plug-in car).',
                                      type: 'text',
                                    },
                                  ],
                                },
                              ],
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'You must remove any personal possessions from the Part Exchange Car before we collect it. We will not be responsible for any personal items lost once the Part Exchange Car is in our possession.',
                              type: 'text',
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'You are responsible for removal, deletion or redaction of your personal information contained in the Part Exchange Car prior to handover of the Part Exchange Car, including as follows:',
                              type: 'text',
                            },
                          ],
                        },
                        {
                          type: 'ordered_list',
                          attrs: {
                            order: 1,
                          },
                          content: [
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'if the Part Exchange Car has a satnav, phone interface or any other device capable of storing data, you will ensure that you have removed all personal data from these devices prior to handing the Part Exchange Car over to us. This includes contacts (names and numbers), previous or favourite destinations in your trip history and default “home” address; and',
                                      type: 'text',
                                    },
                                  ],
                                },
                              ],
                            },
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'you will ensure that your name and address are removed or redacted from any service or maintenance records that you hand over with the Part Exchange Car.',
                                      type: 'text',
                                    },
                                  ],
                                },
                              ],
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'The V5C registration document (which includes your name and address and is an official DVLA document) will remain with the Part Exchange Car until it is sold and re-registered by a new registered keeper or scrapped. Please note that before being re-registered by a new keeper, the Part Exchange Car may be sold to one or more motor traders who will not be obliged to register as the new keeper of the Part Exchange Car and remove your name and address from the V5C registration document. We do not accept any liability in respect of your personal information contained on the V5C registration document.',
                              type: 'text',
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'In the event that you fail to comply with these hand-over requirements, you agree that we have the right to withdraw any offer to buy the Part Exchange Car with immediate effect and/or to indicate a revised valuation at which we will be prepared to purchase the Part Exchange Car and/or cancel the Order in accordance with section 10.15 of these Terms. ',
                              type: 'text',
                            },
                          ],
                        },
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'D: Part Exchange – offering a Part Exchange Car of a higher valuation than the Car  ',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                                {
                                  type: 'bold',
                                },
                              ],
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'This Part D applies where your Part Exchange Car is valued at a higher price than the Purchase Price of the Car you have ordered from us. In these Terms we refer to the difference in the valuation of your Part Exchange Car (after any applicable outstanding finance has been settled) and the Purchase Price of the Car as the ',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                            {
                              text: 'Extra Value',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                                {
                                  type: 'bold',
                                },
                              ],
                            },
                            {
                              text: '.',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'Where you have Extra Value, you can still choose to use your Part Exchange Car in full payment of the Purchase Price. We reserve the right to set a cap on the maximum amount of Extra Value we’ll accept in payment of the Purchase Price of your Car, which may change from time to time. You can find out what (if any) cap applies ',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                            {
                              text: 'here',
                              type: 'text',
                              marks: [
                                {
                                  type: 'link',
                                  attrs: {
                                    href: 'https://www.cinch.co.uk/part-exchange',
                                    uuid: null,
                                    anchor: null,
                                    target: '_self',
                                    linktype: 'url',
                                  },
                                },
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                            {
                              text: '.',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                                {
                                  type: 'bold',
                                },
                              ],
                            },
                            {
                              text: ' ',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'If we accept and collect a downgrade Part Exchange Car at Delivery or Collection, we will pay you the Extra Value. You agree that, prior to payment to you, the amount of the Extra Value will be used to pay:',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                          ],
                        },
                        {
                          type: 'ordered_list',
                          attrs: {
                            order: 1,
                          },
                          content: [
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'the Admin Fee;',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                      ],
                                    },
                                  ],
                                },
                              ],
                            },
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: ' any Transport Charge where due;',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                      ],
                                    },
                                  ],
                                },
                              ],
                            },
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: ' for cinchShield (where you opt to purchase this product - see Part D of section 8);',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                      ],
                                    },
                                  ],
                                },
                              ],
                            },
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: ' any applicable Shortfall Amount (as described in section 10.13); and/or',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                      ],
                                    },
                                  ],
                                },
                              ],
                            },
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: ' any applicable outstanding balance of finance (as described in section 10.14).',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                      ],
                                    },
                                  ],
                                },
                              ],
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'The amount of the Extra Value remaining following the deductions in section 10.23 (if any) will be paid by us to you by electronic transfer to your bank account using the details you provide to us when placing your Order.',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'We will pay any amounts owing under this section within 5 Working Days of us collecting the Part Exchange Car from you. A Working Day is any day when banks are open for business in England and Wales (excluding Saturdays and Sundays). We reserve the right to delay payment in the event we consider it necessary to conduct:',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                          ],
                        },
                        {
                          type: 'ordered_list',
                          attrs: {
                            order: 1,
                          },
                          content: [
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'additional checks on the Part Exchange Car to ensure it meets the requirements under this section 10; and/or',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                      ],
                                    },
                                  ],
                                },
                              ],
                            },
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'know-your-customer checks prior to payment to comply with our legal and regulatory obligations.',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                      ],
                                    },
                                  ],
                                },
                              ],
                            },
                          ],
                        },
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'We won’t delay payment to you of the Extra Value without good reason.',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'The bank account used for receipt of the balance of the Extra Value (following the deductions in section 10.23) must be a valid UK registered bank account held in your name as it appears on the Order and your driving licence. During the Order process you will be asked to:',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                          ],
                        },
                        {
                          type: 'ordered_list',
                          attrs: {
                            order: 1,
                          },
                          content: [
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'confirm that the bank details you have provided are for a UK bank account in your name; and',
                                      type: 'text',
                                    },
                                  ],
                                },
                              ],
                            },
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'acknowledge that if the bank details provided are not correct you may not receive the Extra Value (see section 10.27 for more information).',
                                      type: 'text',
                                    },
                                  ],
                                },
                              ],
                            },
                          ],
                        },
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'If the name on your Order or driving licence is different to your UK registered bank account please contact our customer experience team to discuss your options. The telephone number of our customer experience team can be found ',
                              type: 'text',
                            },
                            {
                              text: 'here',
                              type: 'text',
                              marks: [
                                {
                                  type: 'link',
                                  attrs: {
                                    href: 'https://www.cinch.co.uk/support',
                                    uuid: null,
                                    anchor: null,
                                    target: '_self',
                                    linktype: 'url',
                                  },
                                },
                              ],
                            },
                            {
                              text: '. For example, if your Order and driving licence is in your married name but your bank account is in your maiden name, we may ask you to provide a copy of your marriage certificate.',
                              type: 'text',
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'If you enter any part of your sort code or account number incorrectly during the Order process this may result in a delayed payment to you, or no payment to you if the payment was made to an incorrect party as a result of you providing us with incorrect bank details. If payment is made to the wrong bank account as a result of incorrect bank information being provided by you:',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                          ],
                        },
                        {
                          type: 'ordered_list',
                          attrs: {
                            order: 1,
                          },
                          content: [
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'we will issue a payment recall with our bank and take reasonable steps to retrieve the funds and re-direct the payment accordingly; and',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                      ],
                                    },
                                  ],
                                },
                              ],
                            },
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: ' if we’re unable to retrieve the funds in this instance, we won’t be liable to re-issue such payment to another account.',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                      ],
                                    },
                                  ],
                                },
                              ],
                            },
                          ],
                        },
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'In the event of any payment issues we reserve the right to obtain confirmation of the correct account details from you, usually in the form of a bank account statement in your name.',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'Where, following the deductions in section 10.23, there is no Extra Value remaining and you owe money to cinch, you must pay us that amount within 7 days of us requesting that from you using the details we provide to you.',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'With the exception of cinchShield, you cannot use the Extra Value to pay for any other optional additional products you chose to add to your Order (as detailed in section 8).',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                          ],
                        },
                        {
                          type: 'paragraph',
                        },
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'E: Part Exchange – cash release',
                              type: 'text',
                              marks: [
                                {
                                  type: 'bold',
                                },
                              ],
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'This Part E applies where you are using part of the value of your Part Exchange Car as payment of your deposit for your Finance Agreement and want to be paid the remainder of the value of your Part Exchange Car. In these Terms we refer to the difference in the valuation of your Part Exchange Car (after any applicable outstanding finance has been settled) and the amount you are using as payment of your deposit as the ',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                            {
                              text: 'Cash Release',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                                {
                                  type: 'bold',
                                },
                              ],
                            },
                            {
                              text: '.',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'We reserve the right to set a cap on the maximum amount of Cash Release we will pay to you, which may change from time to time. The applicable amount will be set out clearly when you are proceeding through the Order process when ordering your Car',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                            {
                              text: '.',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                                {
                                  type: 'bold',
                                },
                              ],
                            },
                            {
                              text: ' ',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: ' If you opt to receive a Cash Release as part of your Order and we accept and collect your Part Exchange Car at Delivery or Collection, we will pay you the Cash Release. You agree that, prior to payment to you, the amount of the Cash Release will be used to pay:',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                          ],
                        },
                        {
                          type: 'ordered_list',
                          attrs: {
                            order: 1,
                          },
                          content: [
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: ' the Admin Fee;',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                      ],
                                    },
                                  ],
                                },
                              ],
                            },
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'any Transport Charge where you’ve chosen Express Delivery or Express Collection;',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                      ],
                                    },
                                  ],
                                },
                              ],
                            },
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'for cinchShield (where you opt to purchase this product - see Part D of section 8);',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                      ],
                                    },
                                  ],
                                },
                              ],
                            },
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'any applicable Shortfall Amount (as described in section 10.13); and/or',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                      ],
                                    },
                                  ],
                                },
                              ],
                            },
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'any applicable outstanding balance of finance (as described in section 10.14).',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                      ],
                                    },
                                  ],
                                },
                              ],
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'The amount of the Cash Release remaining following the deductions in section 10.32 (if any) will be paid by us to you by electronic transfer to your bank account using the details you provide to us when placing your Order.',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'We will pay any amounts owing under this section within 5 Working Days of us collecting the Part Exchange Car from you. A Working Day is any day when banks are open for business in England and Wales (excluding Saturdays and Sundays). We reserve the right to delay payment in the event we consider it necessary to conduct:',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                          ],
                        },
                        {
                          type: 'ordered_list',
                          attrs: {
                            order: 1,
                          },
                          content: [
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'additional checks on the Part Exchange Car to ensure it meets the requirements under this section 10; and/or',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                      ],
                                    },
                                  ],
                                },
                              ],
                            },
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'know-your-customer checks prior to payment to comply with our legal and regulatory obligations.  ',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                      ],
                                    },
                                  ],
                                },
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'We will not delay payment to you of the Cash Release without good reason.',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                      ],
                                    },
                                  ],
                                },
                                {
                                  type: 'paragraph',
                                },
                              ],
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'The bank account used for receipt of the balance of the Cash Release (following the deductions in section 10.32) must be a valid UK registered bank account held in your name as it appears on the Order and your driving licence. During the Order process you will be asked to:',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                          ],
                        },
                        {
                          type: 'ordered_list',
                          attrs: {
                            order: 1,
                          },
                          content: [
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'confirm that the bank details you have provided are for a UK bank account in your name; and',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                      ],
                                    },
                                  ],
                                },
                              ],
                            },
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'acknowledge that if the bank details provided are not correct you may not receive the Cash Release (see section 10.36 for more information).',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                      ],
                                    },
                                  ],
                                },
                                {
                                  type: 'paragraph',
                                },
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'If the name on your Order or driving licence is different to your UK registered bank account, please contact our customer experience team to discuss your options. The telephone number of our customer experience team can be found ',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                      ],
                                    },
                                    {
                                      text: 'here',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'link',
                                          attrs: {
                                            href: 'https://www.cinch.co.uk/support',
                                            uuid: null,
                                            anchor: null,
                                            target: '_self',
                                            linktype: 'url',
                                          },
                                        },
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                      ],
                                    },
                                    {
                                      text: '. For example, if your Order and driving licence is in your married name but your bank account is in your maiden name, we may ask you to provide a copy of your marriage certificate.',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                      ],
                                    },
                                  ],
                                },
                                {
                                  type: 'paragraph',
                                },
                              ],
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'If you enter any part of your sort code or account number incorrectly during the Order process this may result in a delayed payment to you, or no payment to you if the payment was made to an incorrect party as a result of you providing us with incorrect bank details. If payment is made to the wrong bank account as a result of incorrect bank information being provided by you:',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                          ],
                        },
                        {
                          type: 'ordered_list',
                          attrs: {
                            order: 1,
                          },
                          content: [
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'we will issue a payment recall with our bank and take reasonable steps to retrieve the funds and re-direct the payment accordingly; and',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                      ],
                                    },
                                  ],
                                },
                              ],
                            },
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'if we are unable to retrieve the funds in this instance, we will not be liable to re-issue such payment to another account.',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                      ],
                                    },
                                  ],
                                },
                                {
                                  type: 'paragraph',
                                },
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'In the event of any payment issues, we reserve the right to obtain confirmation of the correct account details from you, usually in the form of a bank account statement in your name.',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                      ],
                                    },
                                  ],
                                },
                              ],
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'Where, following the deductions in section 10.32, there is no Cash Release balance remaining and you owe money to cinch, you must pay us that amount within 7 days of us requesting that from you using the details we provide to you.',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                          ],
                        },
                        {
                          type: 'paragraph',
                        },
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'F: Part Exchange – recoveries',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                                {
                                  type: 'bold',
                                },
                              ],
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'We will conduct certain checks on the Part Exchange Car after your Order is placed but before Delivery or Collection and also after we take the Part Exchange Car at Delivery or Collection. If, as a result of such checks, we have reasonable cause to believe the Part Exchange Car does not meet the requirements set out in this section 10, including if the information you provided to us about the Part Exchange Car was not true or accurate, then we reserve the right (at our sole discretion) to:',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                          ],
                        },
                        {
                          type: 'ordered_list',
                          attrs: {
                            order: 1,
                          },
                          content: [
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: ' request from you or take steps to obtain any documentation or information we deem necessary to determine whether the Part Exchange Car meets the requirements set out in this section 10 or not. This may result in a delay to Delivery or Collection or of payment to you of any refund amount under section 12.6 or (where applicable) any Extra Value. In that circumstance, we will not have any liability to you for the delay;',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                      ],
                                    },
                                  ],
                                },
                              ],
                            },
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: ' request a payment from you (using the details we provide to you) to cover any reduction in our valuation of the Part Exchange Car as a result of the Part Exchange Car not meeting the requirements in this section 10. The Purchase Price of your Car will not be affected the by change in the valuation of the Part Exchange Car;',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                      ],
                                    },
                                  ],
                                },
                              ],
                            },
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: ' return the Part Exchange Car to you instead of making a payment to you under section 12.6.4b) or c) or 12.6.5b) or c) below; and/or',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                      ],
                                    },
                                  ],
                                },
                              ],
                            },
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'if you fail to pay in full the amount due under section 10.38.2 above:',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                      ],
                                    },
                                  ],
                                },
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'a) ',
                                      type: 'text',
                                    },
                                    {
                                      text: 'if Delivery or Collection has not yet taken place and you do not make the payment within 24 hours of us notifying you of the amount due, cancel your Order in accordance with section 4.4.4; or',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                      ],
                                    },
                                  ],
                                },
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'b) ',
                                      type: 'text',
                                    },
                                    {
                                      text: 'if Delivery or Collection has already taken place and you do not make the payment within 7 days of us notifying you of the amount due, take legal action against you to recover the reduction in value (',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                      ],
                                    },
                                    {
                                      text: 'Recovery Claim',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                        {
                                          type: 'bold',
                                        },
                                      ],
                                    },
                                    {
                                      text: '). A Recovery Claim may include (where applicable) a request for repayment of the Extra Value. You will also have to pay to us on demand all reasonable costs and expenses incurred by us in pursuing a Recovery Claim.',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                      ],
                                    },
                                  ],
                                },
                              ],
                            },
                          ],
                        },
                      ],
                    },
                  ],
                },
              ],
            },
            {
              type: 'list_item',
              content: [
                {
                  type: 'paragraph',
                  content: [
                    {
                      text: ' Finance',
                      type: 'text',
                      marks: [
                        {
                          type: 'bold',
                        },
                      ],
                    },
                  ],
                },
                {
                  type: 'ordered_list',
                  attrs: {
                    order: 1,
                  },
                  content: [
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'We are a credit broker acting as an appointed representative and not a lender. ',
                              type: 'text',
                            },
                            {
                              text: 'We are an appointed representative (reference number: 930922) of ITC Compliance Limited which is authorised and regulated by the FCA for credit broking and insurance distribution and whose reference number is 313486.',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'You may apply for finance from one of our third-party finance providers (',
                              type: 'text',
                            },
                            {
                              text: 'Lender',
                              type: 'text',
                              marks: [
                                {
                                  type: 'bold',
                                },
                              ],
                            },
                            {
                              text: ') to finance the purchase of the Car by choosing the finance option on the Order page. ',
                              type: 'text',
                            },
                            {
                              text: 'Finance is designed to cover the Purchase Price of your Car only so you will be responsible for payment of the Admin Fee, ',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                            {
                              text: 'any Transport Charge ',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                            {
                              text: ' and the cost of any other optional products relating to the purchase of your Car that you chose to add to your Order (for example, cinchShield). ',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                            {
                              text: 'We will introduce you to the Lender or a broker who will introduce you to a Lender. Your application for finance is subject to status and approval by the Lender. To obtain finance you must sign a hire purchase agreement or a personal contract purchase agreement (',
                              type: 'text',
                            },
                            {
                              text: 'Finance Agreement',
                              type: 'text',
                              marks: [
                                {
                                  type: 'bold',
                                },
                              ],
                            },
                            {
                              text: ') with the Lender prior to Delivery or Collection. Any delay in signing the Finance Agreement may delay Delivery or Collection. If you apply for finance and are declined by the Lender, then you must pay for the Car by an alternative method. If you are unable to do so then we will withdraw our acceptance of your Order.',
                              type: 'text',
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'You may not apply for finance from a Lender if it is intended that the Car will mainly be used by someone other than you.',
                              type: 'text',
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'If you enter into a Finance Agreement to finance the purchase of the Car:',
                              type: 'text',
                            },
                          ],
                        },
                        {
                          type: 'ordered_list',
                          attrs: {
                            order: 1,
                          },
                          content: [
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'we will sell the Car to the Lender who will let you use the Car in accordance with the terms of the Finance Agreement;',
                                      type: 'text',
                                    },
                                  ],
                                },
                              ],
                            },
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'the Lender will own the Car until it transfers ownership to you in accordance with the Finance Agreement;',
                                      type: 'text',
                                    },
                                  ],
                                },
                              ],
                            },
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'you will have a right to cancel under section 12 of these Terms provided that the Car meets the Return Conditions set out in section 12.15; and',
                                      type: 'text',
                                    },
                                  ],
                                },
                              ],
                            },
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'you may have a right of withdrawal under your Finance Agreement which is described in section 13.',
                                      type: 'text',
                                    },
                                  ],
                                },
                              ],
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'When we make an introduction to a Lender (whether direct or through a broker) we are not acting independently and an introduction does not amount to independent financial advice. As we only work with a selected panel of Lenders there may be better products available in the market and the rate offered through cinch may not be the cheapest rate available on the open market. We do not charge you a fee for this service. We will receive a commission payment from a Lender should you decide to enter into a Finance Agreement as a result of the introduction we have made. This payment is typically a fixed fee or a fixed percentage of the amount you borrow. The payment we receive may vary between Lenders and product types. Further information in respect of the commission we receive is set out in our commission statement document that will be provided to you prior to you making a finance application through cinch.',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'Finance rates or terms published are provided by Lenders. We are not responsible for any changes to these rates or terms. APRs stated are representative APRs and the actual APR offered to you may differ. All APRs are set by the Lender concerned, but as part of the interest charged to you in your repayments to the Lender, you will pay them for their costs attached to the Finance Agreement. The Lender’s costs include the amount of commission the Lender pays to us.',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                          ],
                        },
                      ],
                    },
                  ],
                },
              ],
            },
            {
              type: 'list_item',
              content: [
                {
                  type: 'paragraph',
                  content: [
                    {
                      text: ' Our 14-day money back guarantee',
                      type: 'text',
                      marks: [
                        {
                          type: 'bold',
                        },
                      ],
                    },
                  ],
                },
                {
                  type: 'ordered_list',
                  attrs: {
                    order: 1,
                  },
                  content: [
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'You may return the Car to us within 14 days from the day after the date of Delivery or Collection without giving us any reason using our 14-day money back guarantee (the ',
                              type: 'text',
                            },
                            {
                              text: 'Guarantee',
                              type: 'text',
                              marks: [
                                {
                                  type: 'bold',
                                },
                              ],
                            },
                            {
                              text: '). If you paid for the Car using a Finance Agreement, you may use the Guarantee provided that the Car meets the Return Conditions set out in section 12.13 below.',
                              type: 'text',
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'If you wish to return the Car in accordance with the Guarantee, you may notify us by calling our customer experience team whose number can be found ',
                              type: 'text',
                            },
                            {
                              text: 'here',
                              type: 'text',
                              marks: [
                                {
                                  type: 'link',
                                  attrs: {
                                    href: 'https://www.cinch.co.uk/support',
                                    uuid: null,
                                    anchor: null,
                                    target: '_self',
                                    linktype: 'url',
                                  },
                                },
                              ],
                            },
                            {
                              text: '. The customer experience team will arrange a date on which we will collect the Car from you at your residential address.',
                              type: 'text',
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'You may also use the cancellation form set out below to return the Car. If you use the cancellation form we will acknowledge receipt of your form by email without delay and we will call you within 3 days to ',
                              type: 'text',
                            },
                            {
                              text: 'arrange a date on which we will collect the Car from you at your residential address',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                            {
                              text: '. ',
                              type: 'text',
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'It may be possible for you to return your Car to a cinch Store, rather than wait for us to collect it from your home. Please call our customer experience team (whose number can be found here) to check whether this is an option for you. Where this is possible our customer experience team will process your return and arrange a date on which you can return your Car to a cinch Store. We are not able to accept a return of your Car at a cinch Store without prior notice as described in this section. We are also not able to accept a return of your Car at any other business premises of cinch or its group. This means you cannot return a Car to a WeBuyAnyCar.com branch ',
                              type: 'text',
                            },
                            {
                              text: 'or a Marshall dealership even where you originally collected your Car from these locations.',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                            {
                              text: ' The process and restrictions described in this section are applicable to all customers, including where you placed your Order while at a Store. ',
                              type: 'text',
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'To meet the Guarantee deadline you should notify us that you are exercising the right to cancel by 8pm on the day the Guarantee period expires (or if this day is a bank holiday, New Year’s Day, Easter Sunday, Christmas Day or Boxing Day, 8pm on the next day that is not included on this list).',
                              type: 'text',
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'If you choose to return the Car under the Guarantee:',
                              type: 'text',
                            },
                          ],
                        },
                        {
                          type: 'ordered_list',
                          attrs: {
                            order: 1,
                          },
                          content: [
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: ' if you paid for the Car in full by Visa or Mastercard debit or credit card(s), we will repay to you the Purchase Price of the Car, the Admin Fee, any Transport Charge previously paid and (where applicable) the price you paid for cinchShield to the same card(s) originally used to pay for the Car and, if you used more than one card, we will repay to each card the amount that you paid from that card (except for any deductions described in section 12.6.6 below which we may make from any card); or',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                      ],
                                    },
                                  ],
                                },
                              ],
                            },
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'if you paid for the Car in full using Pay by Bank, we will repay to you the Purchase Price of the Car, the Admin Fee, any Transport Charge previously paid and (where applicable) the price you paid for cinchShield to the same bank account originally used to pay for the Car (except for any deductions described in section 12.6.6 below); or',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                      ],
                                    },
                                  ],
                                },
                              ],
                            },
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'if you paid for the Car using a Finance Agreement,',
                                      type: 'text',
                                    },
                                  ],
                                },
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'a)   ',
                                      type: 'text',
                                    },
                                    {
                                      text: 'we will repay to you the deposit, the Admin Fee, any Transport Charge previously paid and (where applicable) the price you paid for cinchShield which you paid to us:',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                      ],
                                    },
                                  ],
                                },
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'i    ',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                      ],
                                    },
                                    {
                                      text: '             if you paid these amounts by Visa or Mastercard debit or credit card, we will repay them to the same card(s) originally used to pay such amounts and, if more than one card was used, we will repay each card with the amount originally paid from that card; or',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                      ],
                                    },
                                  ],
                                },
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'ii              ',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                      ],
                                    },
                                    {
                                      text: 'if you paid these amounts using Pay by Bank, we will repay them to the same bank account originally used to pay such amounts.',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                      ],
                                    },
                                  ],
                                },
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'Please note that if you used a Part Exchange Car to pay for all or some of the deposit then section 12.6.4 will apply to any repayment we make to you for the deposit. The deposit repaid to you will not include any deposit contribution which we have made towards your deposit); and',
                                      type: 'text',
                                    },
                                  ],
                                },
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'b)   we will repay the remainder of the Purchase Price (less any deposit contribution made by us) to the finance provider with whom you entered into a Finance Agreement; and',
                                      type: 'text',
                                    },
                                  ],
                                },
                              ],
                            },
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'if you have sold a Part Exchange Car to us in part payment of the Car (including as a deposit for a Finance Agreement):',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                      ],
                                    },
                                  ],
                                },
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'a) we will not be able to return the Part Exchange Car to you;',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                      ],
                                    },
                                  ],
                                },
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'b',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                      ],
                                    },
                                    {
                                      text: ')we will include in the amount to be repaid to you an amount equal to the price we paid to you for the Part Exchange Car (less any Cash Release amount paid to you (as described in section 10.30) and after any outstanding finance on the Part Exchange Car has been settled as described in Part B of section 10 above (including any Shortfall Amount) and subject to the Part Exchange Car meeting the requirements set out in section 10); and',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                      ],
                                    },
                                  ],
                                },
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'c) if your Part Exchange Car was in negative equity and you have made a payment to us in respect of the negative equity amount (as described in section 10.7 above), this amount will be used by us to settle the outstanding finance on the Part Exchange Car and we shall not repay this amount to you; or',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                      ],
                                    },
                                  ],
                                },
                              ],
                            },
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'if you have sold a Part Exchange Car to us where the valuation of the Part Exchange Car was more than the Purchase Price of the Car you purchased from us (as described in section 10.21):',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                      ],
                                    },
                                  ],
                                },
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'a) we will not be able to return the Part Exchange Car to you;',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                      ],
                                    },
                                  ],
                                },
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'b) subject to the Part Exchange Car meeting the requirements set out in section 10, we will repay to you the Purchase Price of the Car, the Admin Fee, any Transport Charge previously paid and (where applicable) the price you paid for cinchShield to the same bank account originally used by us to pay you the Extra Value, after:',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                      ],
                                    },
                                  ],
                                },
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: ' i. accounting for any outstanding finance on the Part Exchange Car that has been settled by us as described in Part B of section 10 above (including any Shortfall Amount); and',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                      ],
                                    },
                                  ],
                                },
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'ii. applying any deductions set out in section 12.6.6;',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                      ],
                                    },
                                  ],
                                },
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'c) if you have paid any Shortfall Amount (as described in section 10.13) by Visa or Mastercard debit or credit cards or using Pay by Bank (in the alternative to a reduction in the Extra Value), this amount will be used by us to settle the outstanding finance on the Part Exchange Car and we shall not repay this amount to you;',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                      ],
                                    },
                                  ],
                                },
                              ],
                            },
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'we will deduct from the money to be repaid to you (as described in sections 12.6.1, 12.6.2, 12.6.3, 12.6.4 and 12.6.5 above):',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                      ],
                                    },
                                  ],
                                },
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'a) any discount that was applied to your Order at the time it was placed because of your participation in a promotion, such as using a discount code;',
                                      type: 'text',
                                    },
                                  ],
                                },
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'b) any amounts which we are permitted to charge you under section 12.7 because of the condition in which you returned the Car to us; and',
                                      type: 'text',
                                    },
                                  ],
                                },
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'c) £99, which we will hold pending you returning the most recent V5C registration document to us in accordance with section 12.8. We need the V5C to be returned because the value of the Car will reduce without it; ',
                                      type: 'text',
                                    },
                                  ],
                                },
                              ],
                            },
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'we will repay any money owed to you under this section 12.6 within 14 days of you informing us that you wish to return the Car; and',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                      ],
                                    },
                                  ],
                                },
                              ],
                            },
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'we won’t charge you any fees in relation to any repayment to you under this section 12.6.  ',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                      ],
                                    },
                                  ],
                                },
                              ],
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'You are entitled to use the Car to determine the nature, characteristics and functioning of the Car. We consider this use to be similar to a couple of test drives which you would receive if you were buying a Car from a dealer face to face. You must not use the Car after notifying us that you are cancelling the Agreement and you must keep the Car in good condition from the date of Delivery or Collection. We are entitled to charge you if the value of the Car has reduced as a result of excessive use by you or any damage to the Car. We may charge you:',
                              type: 'text',
                            },
                          ],
                        },
                        {
                          type: 'ordered_list',
                          attrs: {
                            order: 1,
                          },
                          content: [
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'if the Car has been driven for more than 250 miles since Delivery or Collection, a fee equal to £1.00 for each mile driven in excess of 250 miles (',
                                      type: 'text',
                                    },
                                    {
                                      text: 'Excess Mileage Payment',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'bold',
                                        },
                                      ],
                                    },
                                    {
                                      text: '); and/or',
                                      type: 'text',
                                    },
                                  ],
                                },
                              ],
                            },
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'if the value of the Car has been reduced because it is not in the condition it was in at Delivery or Collection, our reasonable costs to repair the Car so that it is in the condition it was in at Delivery or Collection (',
                                      type: 'text',
                                    },
                                    {
                                      text: 'Damage Payment',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'bold',
                                        },
                                      ],
                                    },
                                    {
                                      text: ').',
                                      type: 'text',
                                    },
                                  ],
                                },
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'For the purposes of this section 12.7, we will record the mileage on the Car at Delivery or Collection and this mileage will be conclusive in determining whether you have driven the Car more than 250 miles since Delivery or Collection.',
                                      type: 'text',
                                    },
                                  ],
                                },
                              ],
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'You must send the most recent V5C registration document showing you as the registered keeper to BCA DocuSafe by recorded delivery within 14 days of you informing us that you wish to return the Car. You will bear the cost of the recorded delivery. Please see our “Money back guarantee“ page ',
                              type: 'text',
                            },
                            {
                              text: 'here',
                              type: 'text',
                              marks: [
                                {
                                  type: 'link',
                                  attrs: {
                                    href: 'https://www.cinch.co.uk/returns',
                                    uuid: null,
                                    anchor: null,
                                    target: '_self',
                                    linktype: 'url',
                                  },
                                },
                              ],
                            },
                            {
                              text: ' for more information. The V5C registration document (which includes your name and address and is an official DVLA document) will remain linked with the Car until it is sold and re-registered by a new registered keeper or scrapped. Please note that before being re-registered by a new keeper, the Car may be sold to one or more motor traders who will not be obliged to register as the new keeper of the Car and remove your name and address from the V5C registration document. We do not accept any liability in respect of your personal information contained on the V5C registration document.',
                              type: 'text',
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'Subject to the V5C being received within 14 days of you informing us that you wish to return the Car, we will repay you the £99 we retained as described in section 12.6.6(c) within 14 days of us receiving the V5C. If you fail to return the V5C registration document, we may charge you a fee of £99 as the value of the Car will reduce without the V5C registration document (',
                              type: 'text',
                            },
                            {
                              text: 'V5C Fee',
                              type: 'text',
                              marks: [
                                {
                                  type: 'bold',
                                },
                              ],
                            },
                            {
                              text: ') and we will retain the £99 referred to in section 12.6.6(c) as payment by you to us for the V5C Fee.',
                              type: 'text',
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'If the total of the Excess Mileage Payment, the Damage Payment and/or the V5C Fee is more than the amount payable to you in section 12.6 then we will tell you how much you owe us (',
                              type: 'text',
                            },
                            {
                              text: 'Outstanding Amount',
                              type: 'text',
                              marks: [
                                {
                                  type: 'bold',
                                },
                              ],
                            },
                            {
                              text: ') and you must pay the Outstanding Amount to us within 30 days of us notifying you of the Outstanding Amount.',
                              type: 'text',
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'You confirm to us that, when you hand the Car back to us, it:',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                          ],
                        },
                        {
                          type: 'ordered_list',
                          attrs: {
                            order: 1,
                          },
                          content: [
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'is free from all charges or claims from any third party including a finance provider (except in respect any finance arranged with a Lender under section 11.2);',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                      ],
                                    },
                                  ],
                                },
                              ],
                            },
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'is owned by you (or the Lender if you paid for the Car using finance arranged under section 11.2;',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                      ],
                                    },
                                  ],
                                },
                              ],
                            },
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'is in the same condition as on Delivery or Collection except for any reasonable wear and tear;',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                      ],
                                    },
                                  ],
                                },
                              ],
                            },
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'has not been altered or modified sinch Delivery or Collection; and',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                      ],
                                    },
                                  ],
                                },
                              ],
                            },
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'has not been involved in an accident since Delivery or Collection.',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                      ],
                                    },
                                  ],
                                },
                              ],
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'If you use the Guarantee we will collect the Car from you on the date agreed with you. You must also give to us:',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                          ],
                        },
                        {
                          type: 'ordered_list',
                          attrs: {
                            order: 1,
                          },
                          content: [
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: "all copies of the Car's keys;",
                                      type: 'text',
                                    },
                                  ],
                                },
                              ],
                            },
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: "the Car's service history (if any);",
                                      type: 'text',
                                    },
                                  ],
                                },
                              ],
                            },
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: "the Car's MOT certificate (if any);",
                                      type: 'text',
                                    },
                                  ],
                                },
                              ],
                            },
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: "the Car's user manuals (if any);",
                                      type: 'text',
                                    },
                                  ],
                                },
                              ],
                            },
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'any accessories there may be such as a locking wheel nut, radio fascia or remote controls, SD card and/or charging cables (if you have a plug-in car); and',
                                      type: 'text',
                                    },
                                  ],
                                },
                              ],
                            },
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'any other documents or items which we gave to you at Delivery or Collection in relation to the Car.',
                                      type: 'text',
                                    },
                                  ],
                                },
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'You are responsible for the removal of your personal belongings or the deletion of personal data from the Car prior to collection in the same manner as for a Part Exchange Car (see section 10.17 and section 10.18).',
                                      type: 'text',
                                    },
                                  ],
                                },
                              ],
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'If you need to rearrange the date and time of your return collection, you must contact the customer experience team before 12pm on the day before your agreed return collection date. If you rearrange your return collection day more than once or you ask to change the date and time of your return collection after 12pm on the day before your agreed return collection date, we may charge you a fee of £150. You must allow us to collect the Car as soon as possible and at the latest within 14 days of you giving notice to cancel. ',
                              type: 'text',
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'You remain responsible for the Car until we have collected it from you. You must ensure that the Car is insured with a fully comprehensive insurance policy until collection.  If we fail to collect the Car on the agreed date and this is not caused by you, then we will reimburse you for the additional days’ insurance costs you have had to incur until we collect the Car. We will require proof of the additional insurance costs before we can reimburse you.',
                              type: 'text',
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'If you have paid for the Car using a Finance Agreement',
                              type: 'text',
                              marks: [
                                {
                                  type: 'bold',
                                },
                              ],
                            },
                            {
                              text: ', you may return the Car to us using the Guarantee provided that the Car meets the following conditions: ',
                              type: 'text',
                            },
                          ],
                        },
                        {
                          type: 'ordered_list',
                          attrs: {
                            order: 1,
                          },
                          content: [
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'the Car is not driven for more than 250 miles since Delivery or Collection;',
                                      type: 'text',
                                    },
                                  ],
                                },
                              ],
                            },
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'no alterations or modifications whatsoever have been made to the Car;',
                                      type: 'text',
                                    },
                                  ],
                                },
                              ],
                            },
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'the Car is in the same condition as it was at Delivery or Collection (except for any reasonable wear and tear); ',
                                      type: 'text',
                                    },
                                  ],
                                },
                              ],
                            },
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'the Car has not been involved in an accident since Delivery or Collection; and',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                      ],
                                    },
                                  ],
                                },
                              ],
                            },
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'the Car is free from all charges or claims from any third party including a finance provider (except any finance arranged with a Lender under section 11.2).',
                                      type: 'text',
                                    },
                                  ],
                                },
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'We refer to these conditions as the ',
                                      type: 'text',
                                    },
                                    {
                                      text: 'Return Conditions',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'bold',
                                        },
                                      ],
                                    },
                                    {
                                      text: '.',
                                      type: 'text',
                                    },
                                  ],
                                },
                              ],
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'If you have paid for the Car using a Finance Agreement',
                              type: 'text',
                              marks: [
                                {
                                  type: 'bold',
                                },
                              ],
                            },
                            {
                              text: ' and the Car does not meet the Return Conditions then we may, in our absolute discretion, allow you to use the Guarantee. If you meet the Return Conditions or we allow you to use the Guarantee, then this section 12 will apply. You must also tell the finance provider that you wish to withdraw from your Finance Agreement. We will not notify the finance provider for you.',
                              type: 'text',
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'If you return the Car using the Guarantee:',
                              type: 'text',
                            },
                          ],
                        },
                        {
                          type: 'ordered_list',
                          attrs: {
                            order: 1,
                          },
                          content: [
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'and you purchased any of our cinchCover products, we will notify Assurant. cinchCover, or any other service contract between you and Assurant that has been arranged through cinch, will be automatically terminated. The terms and conditions of your cinchCover product (as set out in the policy wording documentation you received from Assurant following your purchase) will explain whether or not you will be entitled to a refund from Assurant of the price paid for the product; and',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                      ],
                                    },
                                  ],
                                },
                              ],
                            },
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'and you purchased GAP Insurance, we will notify CCP. Your GAP Insurance contract will be automatically terminated by CCP. The terms and conditions of the GAP Insurance product (as set out in the policy wording documentation you received from us and CCP) will explain whether or not you will be entitled to a refund from CCP of the price paid for the product; and',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: '',
                                          },
                                        },
                                      ],
                                    },
                                  ],
                                },
                              ],
                            },
                            {
                              type: 'list_item',
                              content: [
                                {
                                  type: 'paragraph',
                                  content: [
                                    {
                                      text: 'the 90-day warranty will be automatically terminated.',
                                      type: 'text',
                                      marks: [
                                        {
                                          type: 'styled',
                                          attrs: {
                                            class: 'rgb(0, 0, 0)',
                                          },
                                        },
                                      ],
                                    },
                                  ],
                                },
                              ],
                            },
                          ],
                        },
                      ],
                    },
                  ],
                },
              ],
            },
            {
              type: 'list_item',
              content: [
                {
                  type: 'paragraph',
                  content: [
                    {
                      text: ' Your right of withdrawal (if you have paid for the Car using a Finance Agreement)',
                      type: 'text',
                      marks: [
                        {
                          type: 'bold',
                        },
                      ],
                    },
                  ],
                },
                {
                  type: 'ordered_list',
                  attrs: {
                    order: 1,
                  },
                  content: [
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'Where you have paid for the Car by entering into a Finance Agreement with the Lender, you may have a right to withdraw from the Finance Agreement without giving any reason. You should refer to the terms of your Finance Agreement for more information on your right to withdraw from the Finance Agreement.',
                              type: 'text',
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'If you exercise your right of withdrawal, you will withdraw from the Finance Agreement only. You will be required to pay the amount of credit plus interest to the Lender in accordance with the terms of the Finance Agreement. When you have repaid the Lender the amount of credit plus interest you will own the Car. You should contact the Lender for more information on the right of withdrawal.',
                              type: 'text',
                            },
                          ],
                        },
                      ],
                    },
                  ],
                },
              ],
            },
            {
              type: 'list_item',
              content: [
                {
                  type: 'paragraph',
                  content: [
                    {
                      text: ' If you have a concern with the Car or the cinch service',
                      type: 'text',
                      marks: [
                        {
                          type: 'bold',
                        },
                      ],
                    },
                  ],
                },
                {
                  type: 'ordered_list',
                  attrs: {
                    order: 1,
                  },
                  content: [
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'If you have any questions or complaints about the Car or the service provided by cinch, please contact us. You can telephone our customer service team on 0333 0154 101 or write to us at ',
                              type: 'text',
                            },
                            {
                              text: 'complaints@cinch.co.uk',
                              type: 'text',
                              marks: [
                                {
                                  type: 'link',
                                  attrs: {
                                    href: 'mailto:complaints@cinch.co.uk',
                                    uuid: null,
                                    anchor: null,
                                    target: '_self',
                                    linktype: 'url',
                                  },
                                },
                              ],
                            },
                            {
                              text: '. You can read a copy of our complaints handling process ',
                              type: 'text',
                            },
                            {
                              text: 'here',
                              type: 'text',
                              marks: [
                                {
                                  type: 'link',
                                  attrs: {
                                    href: 'https://www.cinch.co.uk/support',
                                    uuid: null,
                                    anchor: null,
                                    target: '_self',
                                    linktype: 'url',
                                  },
                                },
                              ],
                            },
                            {
                              text: '.',
                              type: 'text',
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'If you have a question or complaint about a new car advertised on cinch, please contact Marshall. You can contact Marshall using the contact information available ',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                            {
                              text: 'here',
                              type: 'text',
                              marks: [
                                {
                                  type: 'link',
                                  attrs: {
                                    href: 'https://www.marshall.co.uk/contact-us/ ',
                                    uuid: null,
                                    anchor: null,
                                    target: '_self',
                                    linktype: 'url',
                                  },
                                },
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                            {
                              text: '. You can read a copy of the Marshall complaints handling process ',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                            {
                              text: 'here',
                              type: 'text',
                              marks: [
                                {
                                  type: 'link',
                                  attrs: {
                                    href: 'https://www.marshall.co.uk/complaints/ ',
                                    uuid: null,
                                    anchor: null,
                                    target: '_self',
                                    linktype: 'url',
                                  },
                                },
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                            {
                              text: '.',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                          ],
                        },
                      ],
                    },
                  ],
                },
              ],
            },
            {
              type: 'list_item',
              content: [
                {
                  type: 'paragraph',
                  content: [
                    {
                      text: ' Summary of your legal rights.',
                      type: 'text',
                      marks: [
                        {
                          type: 'bold',
                        },
                      ],
                    },
                  ],
                },
                {
                  type: 'paragraph',
                  content: [
                    {
                      text: 'We are under a legal duty to supply used cars that are in conformity with this Agreement and your legal rights in relation to faulty goods. Nothing in this Agreement will affect your legal rights. If you would like more information on your rights you should contact your local Citizens Advice Bureau.',
                      type: 'text',
                    },
                  ],
                },
              ],
            },
            {
              type: 'list_item',
              content: [
                {
                  type: 'paragraph',
                  content: [
                    {
                      text: ' Data Protection',
                      type: 'text',
                      marks: [
                        {
                          type: 'bold',
                        },
                      ],
                    },
                  ],
                },
                {
                  type: 'paragraph',
                  content: [
                    {
                      text: 'A summary of how we will use your personal information is set out in our privacy policy which can be found ',
                      type: 'text',
                    },
                    {
                      text: 'here',
                      type: 'text',
                      marks: [
                        {
                          type: 'link',
                          attrs: {
                            href: 'https://www.cinch.co.uk/privacy',
                            uuid: null,
                            anchor: null,
                            target: '_self',
                            linktype: 'url',
                          },
                        },
                      ],
                    },
                    {
                      text: '.',
                      type: 'text',
                    },
                  ],
                },
              ],
            },
            {
              type: 'list_item',
              content: [
                {
                  type: 'paragraph',
                  content: [
                    {
                      text: ' Liability',
                      type: 'text',
                      marks: [
                        {
                          type: 'bold',
                        },
                      ],
                    },
                  ],
                },
                {
                  type: 'ordered_list',
                  attrs: {
                    order: 1,
                  },
                  content: [
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'Nothing in these Terms excludes or limits our liability for death or personal injury arising from our negligence, or our fraud or fraudulent misrepresentation, or any other liability that cannot be excluded or limited by law.',
                              type: 'text',
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'To the extent we are able to restrict our responsibility by law, we will not be responsible to you for any indirect or consequential losses or loss of income or revenue, loss of business, loss of profit, or loss of anticipated savings (whether direct or indirect).',
                              type: 'text',
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'To the extent we are able to exclude our responsibility by law, we exclude all implied representations, warranties, terms and conditions from the Agreement.',
                              type: 'text',
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'Subject to the above, our maximum total liability to you under or in connection with the Order and the Agreement with you shall not exceed the Purchase Price of the Car.',
                              type: 'text',
                            },
                          ],
                        },
                      ],
                    },
                  ],
                },
              ],
            },
            {
              type: 'list_item',
              content: [
                {
                  type: 'paragraph',
                  content: [
                    {
                      text: ' Circumstances outside of our control',
                      type: 'text',
                      marks: [
                        {
                          type: 'bold',
                        },
                      ],
                    },
                  ],
                },
                {
                  type: 'ordered_list',
                  attrs: {
                    order: 1,
                  },
                  content: [
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'We will not be responsible for any failure by us to perform the Agreement or any delay in performing the Agreement which is caused by a circumstance or event outside of our control (',
                              type: 'text',
                            },
                            {
                              text: 'Circumstance Outside of Our Control',
                              type: 'text',
                              marks: [
                                {
                                  type: 'bold',
                                },
                              ],
                            },
                            {
                              text: '). Circumstances or events which may be included in this section are riots, acts of terrorism or war, civil disruption, strikes, fire, flood, other natural disasters, pandemics, banking system failures and accidents relating to the Car. Other circumstances or events may also be included if they are outside of our control.',
                              type: 'text',
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'If our supply of the Car is delayed by a Circumstance Outside of Our Control, then we will contact you as soon as possible to let you know and we will do what we can to reduce the delay. If we do this, we will not be responsible for delays. If there is a delay you may contact us to end the Agreement and receive a refund of the deposit or the Purchase Price you have paid at that time for the Car, together with a refund of the Admin Fee and any Transport Charge you have paid.',
                              type: 'text',
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'If a Circumstance Outside of Our Control occurs which affects us performing the Agreement after we have supplied the Car to you(including paying any sum owing to you under these Terms) we will notify you as soon as possible. Our responsibilities to you under the Agreement will be temporarily delayed and the agreed date by which we must perform our responsibilities will be extended until the Circumstance Outside of Our Control has ended.',
                              type: 'text',
                            },
                          ],
                        },
                      ],
                    },
                  ],
                },
              ],
            },
            {
              type: 'list_item',
              content: [
                {
                  type: 'paragraph',
                  content: [
                    {
                      text: 'Miscellaneous',
                      type: 'text',
                      marks: [
                        {
                          type: 'bold',
                        },
                      ],
                    },
                  ],
                },
                {
                  type: 'ordered_list',
                  attrs: {
                    order: 1,
                  },
                  content: [
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'If a court finds part of the Agreement is illegal, invalid or otherwise unenforceable, the remaining parts of the Agreement will continue in full force and effect.',
                              type: 'text',
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'You may not transfer any of your rights or obligations under the Agreement to any other person. We may transfer our rights or obligations under the Agreement to another person provided that this will not affect your rights under the Agreement.',
                              type: 'text',
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'The Agreement does not give rise to rights that are enforceable by any person who is not party to the Agreement. ',
                              type: 'text',
                            },
                            {
                              text: 'You confirm that you are entering into this Agreement on your own behalf and not for the benefit of any other person, including any third party car finance provider.',
                              type: 'text',
                              marks: [
                                {
                                  type: 'styled',
                                  attrs: {
                                    class: '',
                                  },
                                },
                              ],
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'If we do not exercise or enforce a right under these Terms against you at any time, this does not prevent us from doing so at a later date.',
                              type: 'text',
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'The Agreement sets out the entire agreement between you and us relating to the purchase of the Car. The Agreement will replace any prior discussions or agreement between you and us relating to the purchase of the Car.',
                              type: 'text',
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'These Terms and the Agreement are governed by English law and you and we both agree that we will issue any legal proceedings in connection with any dispute or claim arising in relation to these Terms and the Agreement in the courts of England and Wales.',
                              type: 'text',
                            },
                          ],
                        },
                      ],
                    },
                    {
                      type: 'list_item',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              text: 'If you live in Scotland or Northern Ireland at Delivery or Collection, we or you may issue any legal proceedings in connection with any dispute or claim arising in relation to these Terms and the Agreement in the courts of Scotland or Northern Ireland respectively.',
                              type: 'text',
                            },
                            {
                              type: 'hard_break',
                            },
                          ],
                        },
                      ],
                    },
                  ],
                },
              ],
            },
          ],
        },
        {
          type: 'paragraph',
        },
        {
          type: 'paragraph',
          content: [
            {
              text: 'Last updated: 22 October 2025',
              type: 'text',
            },
          ],
        },
        {
          type: 'paragraph',
        },
        {
          type: 'heading',
          attrs: {
            level: 2,
          },
          content: [
            {
              text: 'Cancellation form',
              type: 'text',
            },
          ],
        },
        {
          type: 'paragraph',
          content: [
            {
              text: 'To: Cinch Cars Limited of Form 2, 18 Bartley Wood Business Park, Bartley Way, Hook, Hampshire RG27 9XA, email address: ',
              type: 'text',
            },
            {
              text: 'hello@cinch.co.uk',
              type: 'text',
              marks: [
                {
                  type: 'link',
                  attrs: {
                    href: 'mailto:hello@cinch.co.uk',
                    uuid: null,
                    anchor: null,
                    target: '_self',
                    linktype: 'url',
                  },
                },
              ],
            },
            {
              text: ':',
              type: 'text',
            },
          ],
        },
        {
          type: 'paragraph',
          content: [
            {
              text: 'I hereby give notice that I cancel my contract of sale of the following goods.',
              type: 'text',
            },
          ],
        },
        {
          type: 'paragraph',
          content: [
            {
              text: 'Car manufacturer:',
              type: 'text',
              marks: [
                {
                  type: 'bold',
                },
              ],
            },
          ],
        },
        {
          type: 'paragraph',
          content: [
            {
              text: 'Car model:',
              type: 'text',
              marks: [
                {
                  type: 'bold',
                },
              ],
            },
          ],
        },
        {
          type: 'paragraph',
          content: [
            {
              text: 'Registration number:',
              type: 'text',
              marks: [
                {
                  type: 'bold',
                },
              ],
            },
          ],
        },
        {
          type: 'paragraph',
          content: [
            {
              text: 'Received on:',
              type: 'text',
              marks: [
                {
                  type: 'bold',
                },
              ],
            },
          ],
        },
        {
          type: 'paragraph',
          content: [
            {
              text: 'Name of consumer(s):',
              type: 'text',
              marks: [
                {
                  type: 'bold',
                },
              ],
            },
          ],
        },
        {
          type: 'paragraph',
          content: [
            {
              text: 'Address of consumer(s):',
              type: 'text',
              marks: [
                {
                  type: 'bold',
                },
              ],
            },
          ],
        },
        {
          type: 'paragraph',
          content: [
            {
              text: 'Contact number of consumer:',
              type: 'text',
              marks: [
                {
                  type: 'bold',
                },
              ],
            },
          ],
        },
        {
          type: 'paragraph',
          content: [
            {
              text: 'Date:',
              type: 'text',
              marks: [
                {
                  type: 'bold',
                },
              ],
            },
          ],
        },
      ],
    }),
    title: 'Car Purchase Terms and Conditions',
    clauses: [],
    component: 'PolicyPage',
    cookiePolicy: [],
  },
  slug: 'terms-and-conditions',
  full_slug: 'policies/terms-and-conditions',
  sort_by_date: null,
  position: -180,
  tag_list: [],
  is_startpage: false,
  parent_id: 170758293,
  meta_data: null,
  group_id: '0c3514a0-c6f0-4677-b891-4745e9ed6628',
  first_published_at: '2022-08-08T09:55:08.948Z',
  lang: 'default',
  path: '',
  alternates: [],
}
